/**
 * SLF™ Accounting — operator UI strings (en / zh / th).
 *
 * The statutory books module was written with its labels inline, mostly English with hand-built
 * bilingual pairs ("งบทดลอง · Trial balance"). Those are split here: the Thai half of such a pair is
 * the STATUTORY NAME of a book, a statement, a register or an RD form, and it stays in the TSX as a
 * literal because it is what the server prints on the document face. An accountant working in the
 * Chinese UI still has to see which Thai document they are about to produce. Only the gloss beside
 * it, and the chrome around it, live in this file.
 *
 * ⛔ Three rules this file is held to:
 *
 * 1. NO LAW IS INVENTED, in any language. Where a string states a legal rule, every number, form
 *    code, section reference and year inside it is reproduced exactly — ภ.ง.ด.50 stays ภ.ง.ด.50,
 *    พ.ศ. 2566 stays 2566 and does not become 2023, "five-year" stays five. The sentence around
 *    them is translated; the facts are not re-expressed.
 *
 * 2. AN EMPTY VALUE MEANS "fall back to English", NEVER "render nothing". src/i18n/index.ts sets
 *    returnEmptyString: false for exactly this reason (and the comment there records that there
 *    were zero existing empty values when it was flipped). So a gloss we have no good Chinese or
 *    Thai word for degrades to the English — it never leaves a hole where a label should be.
 *
 * 3. SERVER STRINGS ARE NOT IN HERE. The warnings and cover issues the bound-statements endpoint
 *    returns, account names from the chart, a client's name, a bank volume's label: all data. They
 *    are what will actually be printed, and translating them on the screen would show the operator
 *    one wording and print another.
 *
 * Chinese is the vocabulary a Thai firm's Chinese-speaking accounting staff use (会计期间, 过账,
 * 日记账, 试算表, 会计科目表), not a literal gloss of the English. Thai is ordinary operating Thai,
 * not invented officialese.
 */

export const acctEn: Record<string, string> = {
  // ── Accounting period: opening, closing ──────────────────────────────────────────────────────
  pa_sb_period_label: 'Accounting period',
  pa_sb_open_books_gloss: 'Open the books',
  pa_sb_open_books_why: 'The Accounting Act requires the accounts to be closed every twelve months, so a period has to exist before anything can be posted. Leave the end date blank for the ordinary twelve months; a FIRST period may be shorter, and any later period that is not twelve months needs the Chief Accounts Inspector’s written permission recorded first. The firm’s chart of accounts is copied in on the way.',
  pa_sb_from: 'From',
  pa_sb_to: 'To',
  pa_sb_optional: 'optional',
  pa_sb_open_period: 'Open period',
  pa_sb_open_next: 'Open next period',
  pa_sb_status_closed: 'closed',
  // ⛔ _one / _other, NOT _plural: this project is on i18next 23 with no compatibilityJSON, so
  // JSON v4 pluralisation applies and a _plural suffix is silently ignored (checked against the
  // real library, not assumed). zh and th have one plural form, so they carry only _other.
  pa_sb_n_entries_one: '{{count}} entry',
  pa_sb_n_entries_other: '{{count}} entries',
  pa_sb_nothing_posted: 'Nothing has been posted to this period yet — generate the drafts above, review them, and post.',

  pa_sb_close_gloss: 'Close period…',
  pa_sb_close_confirm: 'Closing this period cannot be undone. The result is transferred to retained earnings, the balances carried forward are fixed, and the five-year retention clock starts on the period’s vouchers. Continue?',
  pa_sb_close_sum: 'Income {{income}} − expenses {{expense}} =',
  pa_sb_profit: 'profit',
  pa_sb_loss: 'loss',
  pa_sb_close_drafts_one: '{{count}} draft entry is still waiting — clear it first.',
  pa_sb_close_drafts_other: '{{count}} draft entries are still waiting — clear them first.',
  pa_sb_close_outofrange_one: '{{count}} posted entry falls outside the period.',
  pa_sb_close_outofrange_other: '{{count}} posted entries fall outside the period.',
  pa_sb_close_unbalanced: 'The period does not balance.',
  pa_sb_close_it: 'Close it',
  pa_sb_closed_detail: '{{entries}} entries · {{result}} {{amount}} · {{carried}} balances carried forward · {{vouchers}} vouchers stamped for five-year retention.',

  // ── Section headings. Each sits after a statutory Thai name, as its gloss. ───────────────────
  pa_sb_h_journals: 'Daily journals',
  pa_sb_h_ledger: 'Ledger & trial balance',
  pa_sb_h_statements: 'Financial statements',
  pa_sb_h_extras: 'Cash flow, notes, stock and VAT',

  // ── The cards. The Thai name is in the TSX; these are the lines under it. ────────────────────
  pa_sb_c_gl: 'General ledger',
  pa_sb_c_tb: 'Trial balance',
  pa_sb_c_ar: 'Debtors — subsidiary ledger',
  pa_sb_c_ap: 'Creditors — subsidiary ledger',
  pa_sb_c_stock: 'Stock account — goods clients only',
  pa_sb_c_goods: 'Goods and raw materials report',
  pa_sb_c_vatout: 'Sales tax report — this month',
  pa_sb_c_vatin: 'Purchase tax report — this month',

  // ── The headline figures above the statements ────────────────────────────────────────────────
  pa_sb_pl_line: 'Income {{income}} − expenses {{expense}}',
  pa_sb_assets_line: 'Assets {{amount}}',
  pa_sb_unbalanced_why: 'assets do not equal liabilities plus equity',

  // ── The bound set ────────────────────────────────────────────────────────────────────────────
  pa_fs_bound_what: 'Financial statements — bound set: cover, contents with page numbers, one run of pages',
  pa_fs_bound_build: 'Build the set',
  pa_fs_bound_building: 'Binding…',
  pa_fs_cover_issues: 'printed on the cover',
  pa_fs_pick_least: 'Pick at least one statement.',
  pa_fs_sfp: 'Statement of financial position',
  pa_fs_pl: 'Statement of profit or loss',
  pa_fs_sce: 'Statement of changes in equity',
  pa_fs_cf: 'Statement of cash flows',
  pa_fs_notes: 'Notes to the financial statements',
  // ⛔ ภ.ง.ด.50 is the form code. It is not translated, transliterated or expanded in any locale.
  pa_fs_wa: 'Working account — filed with ภ.ง.ด.50',

  // ── The footnote. States what these documents are and are NOT. ───────────────────────────────
  pa_sb_foot_books: 'Books print in Thai with a cover showing {{client}}, the kind of book and its volume, and consecutive page numbers — the form the DBD’s 2544 notification prescribes.',
  pa_sb_foot_statements: 'The statements are built from those books on the accrual basis. They are NOT yet arranged into the abbreviated line items (รายการย่อ) the DBD’s 2566 notification prescribes, so they are not the form filed with the DBD — that mapping is waiting on the notification’s official text.',

  // ── AccountingBooks — the client picker ───────────────────────────────────────
  pa_books_all_clients: 'All clients',
  pa_books_title: 'Statutory books',
  pa_books_intro: 'Chart of accounts, accounting periods, the five daily journals, the general ledger, closing the year and the financial statements. Every accounting client is listed — including those with no invoices yet, because a newly incorporated company is exactly the one whose books need opening.',
  pa_books_search_ph: 'Search clients…',
  pa_books_empty: 'No accounting clients match.',
  pa_books_drafts_pill: '{{n}} in draft',
  pa_books_no_period: 'No accounting period yet',
  pa_books_pill_closed: 'Closed',
  pa_books_pill_open: 'Open',
  pa_books_card_counts: '{{accounts}} accounts · {{entries}} posted entries',
  pa_books_card_vat: 'VAT',

  // ── Bookkeeping — the invoice-line-item books ─────────────────────────────────
  pa_bk_mon_jan: 'Jan',
  pa_bk_mon_feb: 'Feb',
  pa_bk_mon_mar: 'Mar',
  pa_bk_mon_apr: 'Apr',
  pa_bk_mon_may: 'May',
  pa_bk_mon_jun: 'Jun',
  pa_bk_mon_jul: 'Jul',
  pa_bk_mon_aug: 'Aug',
  pa_bk_mon_sep: 'Sep',
  pa_bk_mon_oct: 'Oct',
  pa_bk_mon_nov: 'Nov',
  pa_bk_mon_dec: 'Dec',
  pa_bk_individual: 'Individual',
  pa_bk_item_remove: 'Delete',
  pa_bk_zero_rate: '0%',
  pa_bk_loading: 'Loading…',
  pa_bk_books_link: 'Statutory books',
  pa_bk_books_link_title: 'Chart of accounts, daily journals, general ledger, closing and financial statements',
  pa_bk_err_items_load: 'Could not load the items',
  pa_bk_err_not_saved: 'Not saved',
  pa_bk_err_add: 'Could not add',
  pa_bk_err_delete: 'Could not delete',
  pa_bk_err_clear: 'Could not clear',
  pa_bk_err_regen: 'Regenerate failed',
  pa_bk_err_month_load: 'Could not load this month',
  pa_bk_err_photo: 'Could not load the photo',

  // ── EntityInfo — juristic form and dividends ──────────────────────────────────
  pa_entity_form_not_recorded: 'Juristic form not recorded',
  pa_entity_unknown_why: 'Whether this client must keep accounts under s.8, which DBD form its statements take, and whether an audit may be waived all depend on this.',
  pa_entity_must_keep: 'Must keep accounts (ม.8)',
  pa_entity_not_required: 'Not a person required to keep accounts',
  pa_entity_dbd_form: 'DBD แบบ {{n}}',
  pa_entity_reg_capital_val: 'Registered capital {{amount}}',
  pa_entity_registered_on_val: 'Registered {{date}}',
  pa_entity_dividend: 'Dividend',
  pa_entity_f_form: 'Juristic form',
  pa_entity_f_form_select: '— Select —',
  pa_entity_form_dbd_suffix: '(แบบ {{n}})',
  pa_entity_f_capital: 'Registered capital (บาท)',
  pa_entity_f_registered_on: 'Registration date',
  pa_entity_div_law: 'ประมวลกฎหมายแพ่งและพาณิชย์ ม.1201–1202 — a dividend is paid only out of profits, never while an accumulated loss stands, and at least 5% of it goes to the legal reserve until that reserve reaches a tenth of registered capital. It must be paid within one month of the resolution.',
  pa_entity_div_amount: 'Amount (บาท)',
  pa_entity_div_resolution_date: 'Resolution date',
  pa_entity_div_payable_account: 'Dividend payable account',
  pa_entity_div_retained: 'Retained earnings {{amount}}',
  pa_entity_div_reserve: 'Legal reserve {{amount}}',
  pa_entity_div_ceiling: 'ceiling {{amount}}',
  pa_entity_div_appropriation: 'Appropriation to legal reserve {{amount}}',
  pa_entity_div_create: 'Create draft',
  pa_entity_div_created: '{{doc}} created as a draft.',
  pa_entity_div_created_appr: '{{doc}} created as a draft, with {{amount}} appropriated to the legal reserve.',

  // ── DraftEntries — generate, review, post ─────────────────────────────────────
  pa_jnl_cash: 'เงินสด',
  pa_jnl_bank: 'ธนาคาร',
  pa_jnl_purchase: 'รายวันซื้อ',
  pa_jnl_sale: 'รายวันขาย',
  pa_jnl_general: 'รายวันทั่วไป',
  pa_draft_title: 'Draft entries',
  pa_draft_sub_pending: 'Nothing here is in the books until you post it.',
  pa_draft_sub_empty: 'Derived from the VAT sub-ledger — invoices and bills already recorded.',
  pa_draft_generate: 'Generate from sub-ledger',
  pa_draft_hide: 'Hide',
  pa_draft_review: 'Review',
  pa_draft_post_all: 'Post all {{n}}',
  pa_draft_post: 'Post',
  pa_draft_discard_title: 'Discard this draft',
  pa_draft_discard_confirm: 'Discard the draft for {{docNo}}? The sub-ledger row stays — you can generate it again.',
  pa_draft_gen_created: 'Drafts created: {{n}}',
  pa_draft_gen_skipped: 'Already had a draft: {{n}}',
  pa_draft_gen_needs_account: 'Need an expense account chosen: {{n}}',
  pa_draft_gen_blocked: 'Lines refused: {{n}} — {{list}}',
  pa_draft_gen_blocked_more: ' … and {{n}} more',
  pa_draft_no_doc_no: '(no number)',
  pa_draft_posted_single: 'Posted.',
  pa_draft_posted_n: 'Posted: {{n}}',

  // ── ChartOfAccounts ───────────────────────────────────────────────────────────
  pa_chart_title: 'Chart of accounts',
  pa_chart_counts: '{{n}} accounts · {{used}} in use',
  pa_chart_empty: 'Empty — copy the firm template to start.',
  pa_chart_copy_template: 'Copy firm template',
  pa_chart_sync_template: 'Sync from template',
  pa_chart_sync_title: 'Add any template account this client does not have yet',
  pa_chart_add_account: 'Add account',
  pa_chart_open: 'Open',
  pa_chart_hide: 'Hide',
  pa_chart_ph_code: 'Code',
  pa_chart_ph_name_th: 'Account name (Thai)',
  pa_chart_ph_name_en: 'English name',
  pa_chart_save: 'Save',
  pa_chart_cancel: 'Cancel',
  pa_chart_rename: 'Rename',
  pa_chart_switch_off: 'Off',
  pa_chart_switch_on: 'On',
  pa_chart_name_th_manual: 'The Thai name is the account-code translation manual required by ม.21(1) — required',
  pa_chart_template_complete: 'The chart already has every account in the firm template.',
  pa_chart_remove_confirm: 'Remove account {{code}} {{name}}?',
  pa_chart_class_asset: 'Assets',
  pa_chart_class_liability: 'Liabilities',
  pa_chart_class_equity: 'Equity',
  pa_chart_class_income: 'Income',
  pa_chart_class_expense: 'Expenses',
  pa_chart_cf_prefixed: 'งบกระแสเงินสด: {{section}}',
  pa_chart_cf_none: '— not set —',
  pa_chart_cf_operating: 'Operating',
  pa_chart_cf_investing: 'Investing',
  pa_chart_cf_financing: 'Financing',
  pa_chart_cf_cash: 'Cash',
  pa_chart_cf_noncash: 'Non-cash',
  pa_chart_cf_missing_warn: '⚠️ An asset, liability or equity account with no cash-flow section will leave the cash flow statement out of balance — by exactly the amount that moved through this account.',
  pa_chart_cf_missing_title: 'The cash flow statement will not balance until this account is given a section',
  pa_chart_cf_missing_pill: 'No งบกระแสเงินสด section',

  // ── CompliancePanel — Accounting Act records ──────────────────────────────────
  pa_compliance_title: 'Audit and DBD filing',
  pa_compliance_file_by: 'File by {{date}}',
  pa_compliance_due_unknown: 'The filing deadline cannot be worked out yet',
  pa_compliance_record_meeting_audit: 'Record meeting / audit',
  pa_compliance_record_filing: 'Record filing',
  pa_compliance_audit_unknown: '⚠ Whether an audit is required is still UNKNOWN',
  pa_compliance_audit_required: 'Must be audited by a Certified Public Accountant (CPA)',
  pa_compliance_audit_exempt: 'Exempt from audit',
  pa_compliance_unbacked: '{{n}} entries with no document, no number and no stated basis. They cannot be posted (ข้อ 5).',
  pa_compliance_filed_msg: '{{kind}} recorded as filed on {{date}}.',
  pa_compliance_agm_due: 'General meeting by {{date}}',
  pa_compliance_agm_held: 'met on {{date}}',
  pa_compliance_agm_not_held: 'not met yet',
  pa_compliance_statements_due: 'statements to shareholders by {{date}}',
  pa_compliance_bojo5_due: 'File บอจ.5 by {{date}}',
  pa_compliance_filed_label: 'Filed:',
  pa_compliance_bookkeeper_label: 'Bookkeeper:',
  pa_compliance_tfac: '(TFAC {{no}})',
  pa_compliance_tfac_lapsed: 'membership lapsed',
  pa_compliance_no_bookkeeper: 'None appointed (Accounting Act s.19)',
  pa_compliance_eacct_pending: 'e-Accountant notification not yet made for {{name}} (within 30 days of {{date}})',
  pa_compliance_notified_today: 'Notified today',
  pa_compliance_f_agm_date: 'General meeting date',
  pa_compliance_f_statements_sent: 'Statements sent to shareholders',
  pa_compliance_f_audit_completed: 'Audit completed',
  pa_compliance_f_opinion: 'Opinion',
  pa_compliance_opinion_unqualified: 'Unqualified',
  pa_compliance_opinion_qualified: 'Qualified',
  pa_compliance_opinion_adverse: 'Adverse',
  pa_compliance_opinion_disclaimer: 'Disclaimer',
  pa_compliance_f_auditor: 'Auditor',
  pa_compliance_f_auditor_license: 'Registration no.',
  pa_compliance_f_auditor_kind: 'Type',
  pa_compliance_auditor_cpa: 'Certified Public Accountant (CPA)',
  pa_compliance_auditor_ta: 'Tax Auditor (TA)',
  pa_compliance_f_audit_fee: 'Audit fee',
  pa_compliance_save: 'Save',
  pa_compliance_f_kind: 'What is being filed',
  pa_compliance_kind_fs: 'งบการเงิน',
  pa_compliance_kind_fs_gloss: 'Financial statements',
  pa_compliance_kind_bojo5: 'บอจ.5',
  pa_compliance_kind_bojo5_gloss: 'Shareholder list',
  pa_compliance_f_filed_on: 'Date filed',
  pa_compliance_f_receipt_no: 'เลขที่ใบรับ · Receipt no.',
  pa_compliance_record: 'Record',

  // ── FixedAssets — the asset register ──────────────────────────────────────────
  pa_assets_register_name: 'ทะเบียนทรัพย์สิน',
  pa_assets_title: 'Fixed assets',
  pa_assets_summary: '{{n}} asset(s) · {{nbv}} net book value',
  pa_assets_empty: 'The register is empty.',
  pa_assets_due: '{{amount}} depreciation to charge this period',
  pa_assets_open_register: 'Register',
  pa_assets_charge_dep: 'Charge depreciation {{amount}}',
  pa_assets_add: 'Fixed asset',
  pa_assets_open: 'Open',
  pa_assets_hide: 'Hide',
  pa_assets_registered_msg: '{{no}} registered at {{rate}}% a year.',
  pa_assets_dep_draft: '{{n}} asset(s), {{amount}} — created as a draft for review.',
  pa_assets_dep_none: 'Every asset has already been charged for this period.',
  pa_assets_f_class: 'Class',
  pa_assets_no_dep: 'Not depreciated',
  pa_assets_f_acquired: 'Acquired on',
  pa_assets_f_cost: 'Cost',
  pa_assets_f_salvage: 'Salvage value',
  pa_assets_f_rate: 'Rate %',
  pa_assets_f_name_th: 'Item (Thai) — required',
  pa_assets_f_name_en: 'Description (English)',
  pa_assets_f_acct_asset: 'Asset account',
  pa_assets_f_acct_accum: 'Accumulated depreciation',
  pa_assets_f_acct_expense: 'Depreciation expense',
  pa_assets_max_rate_source: 'Maximum rate per {{source}}',
  pa_assets_cancel: 'Cancel',
  pa_assets_submit: 'Register',
  pa_assets_th_no: 'No.',
  pa_assets_th_item: 'Item',
  pa_assets_th_acquired: 'Acquired',
  pa_assets_th_cost: 'Cost',
  pa_assets_th_accum: 'Accum. depreciation',
  pa_assets_th_nbv: 'Net book value',
  pa_assets_th_year: 'This year',
  pa_assets_disposed_on: 'Disposed {{date}}',
  pa_assets_days: '{{n}}d',

  // ── ManualEntry — the general journal ─────────────────────────────────────────
  pa_manual_book_general_gloss: 'General',
  pa_manual_book_cash_gloss: 'Cash',
  pa_manual_book_bank_gloss: 'Bank',
  pa_manual_book_purchase_gloss: 'Purchase',
  pa_manual_book_sale_gloss: 'Sale',
  pa_manual_title: 'Adjustments and corrections',
  pa_manual_subtitle: 'Accruals, prepayments, depreciation and provisions — and the reversal that is the only way to correct a posted entry.',
  pa_manual_new_btn: 'Record an entry',
  pa_manual_reverse_btn: 'Reverse',
  pa_manual_sweep_title: 'Bring in accruals from earlier periods that reverse into this one',
  pa_manual_accruals_btn: 'Accruals',
  pa_manual_late_entries: 'Entries posted more than 15 days after their date: {{n}} — the latest by {{days}} days ({{docNo}}).',
  pa_manual_late_rule: 'The DBD requires the daily journals to be written up within 15 days of the transaction.',
  pa_manual_closing_overdue: 'This period ended {{date}}, {{days}} days ago, and is still open. The accounts are to be closed within 60 days.',
  pa_manual_saved: 'Draft {{docNo}} created — review it below and post it.',
  pa_manual_reverse_confirm: 'Reverse {{docNo}}? The original stays in its book with its page number; the correction is created as a draft beside it.',
  pa_manual_reversed_msg: '{{docNo}} created as a draft, dated {{date}}.',
  pa_manual_sweep_done: '{{n}} accrual reversal(s) created as drafts.',
  pa_manual_sweep_none: 'No accrual from an earlier period reverses into this one.',
  pa_manual_f_date: 'Date',
  pa_manual_f_book: 'Journal',
  pa_manual_f_bank_acct: 'Bank account number',
  pa_manual_f_bank_acct_hint: 'one book per account',
  pa_manual_f_bank_acct_ph: 'e.g. 230-3-57579-3',
  pa_manual_bank_acct_new: 'Another account…',
  pa_manual_bank_acct_used: '{{n}} so far',
  pa_manual_bank_acct_none: 'No bank book has been kept for this client yet — type the account number and it will be offered next time.',
  pa_manual_f_docno: 'Document number',
  pa_manual_f_docno_hint: '(blank → JV number)',
  pa_manual_f_docno_ph: 'JV-… allocated',
  pa_manual_f_reverse_on: 'Reverse on',
  pa_manual_f_reverse_on_hint: '(accrual)',
  pa_manual_narration_ph: 'Description — required',
  pa_manual_calcnote_ph: 'How the amount was arrived at — required when there is no supporting document (ข้อ 4)',
  pa_manual_th_account: 'Account',
  pa_manual_th_dr: 'Debit',
  pa_manual_th_cr: 'Credit',
  pa_manual_pick_account: '— Select an account —',
  pa_manual_add_line: 'Line',
  pa_manual_no_amounts: 'No amounts yet',
  pa_manual_balances: 'Balances',
  pa_manual_out_by: 'Out by {{amount}}',
  pa_manual_save_draft: 'Save as draft',
  pa_manual_reverse_note: 'A posted entry is never unposted. It keeps its place and its page number, and the correction is a new entry beside it — which is what leaves an audit trail.',
  pa_manual_search_ph: 'Search posted entries…',
  pa_manual_none_posted: 'Nothing posted matches.',
  pa_manual_reversed_by: 'Reversed {{docNo}}',

  // ── Headless — the records with no other home ─────────────────────────────────
  pa_headless_wa_rule_a: 'Revenue Code s.68 bis — for tax purposes every company prepares a balance sheet, a ',
  pa_headless_wa_rule_b: 'working account',
  pa_headless_wa_rule_c: ' and a profit-and-loss account. ⚠️ The DBD\'s official layout is not held here, so this prints in this system\'s own form.',
  pa_headless_wa_income: 'Income',
  pa_headless_wa_cost: 'Cost of sales',
  pa_headless_wa_gross: 'Gross profit',
  pa_headless_wa_expenses: 'Expenses',
  pa_headless_wa_net: 'Net profit / (loss)',
  pa_headless_wa_mismatch_name: 'งบทดลอง',
  pa_headless_wa_mismatch: 'this does not agree with the trial balance.',
  pa_headless_wa_doc_name: 'บัญชีทำการ',
  pa_headless_wa_doc: 'Working account',
  pa_headless_fw_rule: 'Forms 1, 2, 4 and 5 are built on TFRS for NPAEs; Form 3 on TFRS for PAEs. An NPAE may elect TFRS for PAEs if it applies them in full, and must disclose the change of format in the notes.',
  pa_headless_fw_period: 'Recorded against this period',
  pa_headless_fw_period_tail: ' — a closed period is never restated.',
  pa_headless_fw_none: '— not recorded —',
  pa_headless_fw_npae: 'TFRS สำหรับ NPAEs',
  pa_headless_fw_pae: 'TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ',
  pa_headless_fw_dbd_form: 'DBD แบบ {{form}}',
  pa_headless_fw_locked: 'Closed — locked',
  pa_headless_sh_rule: 'CCC — a company keeps a share register from incorporation and files บอจ.5 as at the meeting date within 14 days. ⚠️ The Registrar\'s blank บอจ.5 is not held here, so the list can be read and checked but not printed onto the official form.',
  pa_headless_sh_as_at: 'As at',
  pa_headless_sh_holders: '{{n}} shareholders',
  pa_headless_sh_shares: '{{n}} shares',
  pa_headless_sh_form_name: 'บอจ.5',
  pa_headless_sh_form_not_held: 'form not held',
  pa_headless_sh_name_ph: 'Shareholder name',
  pa_headless_sh_individual: 'Individual',
  pa_headless_sh_juristic: 'Juristic person',
  pa_headless_sh_idno: 'ID number',
  pa_headless_sh_nationality: 'Nationality',
  pa_headless_sh_shares_ph: 'Number of shares',
  pa_headless_sh_paid_per_share: 'Paid per share',
  pa_headless_sh_held_from: 'Held from',
  pa_headless_sh_add: 'Add',
  pa_headless_sh_th_name: 'Name',
  pa_headless_sh_th_kind: 'Kind',
  pa_headless_sh_th_shares: 'Shares',
  pa_headless_sh_end_prompt: 'Holding ended on (YYYY-MM-DD)?',
  pa_headless_sh_end: 'End…',
  pa_headless_sh_empty: 'No holdings recorded at this date.',
  pa_headless_pkg_rule: 'Financial statements and the auditor\'s report are filed through DBD e-Filing: the statements in XBRL, the auditor\'s report as a black-and-white scan, and the shareholder list.',
  pa_headless_pkg_unit: 'Unit',
  pa_headless_pkg_file: 'file {{id}}',
  pa_headless_pkg_declared: 'declared',
  pa_headless_pkg_not_attached: 'not attached',
  pa_headless_pkg_not_producible: 'not producible here',
  pa_headless_pkg_file_prompt: 'File ID for {{slot}}?',
  pa_headless_pkg_attach: 'Attach…',
  pa_headless_pkg_declare_bw: 'Declare the auditor\'s report black-and-white',
  pa_headless_cit_rule: 'Revenue Code s.65 ter — which accounts are deductible for corporate income tax and which are not. ⚠️ Only subsections (1)–(6) are held here; the rest of s.65 ter has not been obtained.',
  pa_headless_cit_th_account: 'Account',
  pa_headless_cit_th_movement: 'Movement',
  pa_headless_cit_th_treatment: 'CIT treatment',
  pa_headless_cit_th_note: 'Note',
  pa_headless_cit_undecided: '— not decided —',
  pa_headless_cit_yes: 'Deductible',
  pa_headless_cit_no: 'Not deductible',
  pa_headless_cit_limited: 'Deductible in part',
  pa_headless_cit_empty: 'Nothing to review for this period.',
  pa_headless_fx_rule_a: 'Revenue Code s.86/4 — a tax invoice is in Thai baht unless the Revenue Department approves another currency. ',
  pa_headless_fx_rule_b: 'This gate is now enforced when a tax invoice is issued or edited',
  pa_headless_fx_rule_c: ': until an approval is recorded here, a non-THB tax invoice is refused.',
  pa_headless_fx_approval_no: 'RD approval no.',
  pa_headless_fx_approved_on: 'Approved on',
  pa_headless_fx_effective_from: 'Effective from',
  pa_headless_fx_effective_to: 'To',
  pa_headless_fx_recorded: 'Approval recorded for {{currency}}.',
  pa_headless_fx_record: 'Record approval',
  pa_headless_fx_check: 'Check {{currency}} today',
  pa_headless_fx_allowed: 'Allowed.',
  pa_headless_fx_not_allowed: 'Not allowed.',
  pa_headless_ret_label: 'Retention period',
  pa_headless_ret_5y: '5 years (the minimum under มาตรา 14)',
  pa_headless_ret_6y: '6 years',
  pa_headless_ret_7y: '7 years (the most the DBD can extend to)',
  pa_headless_ret_th_when: 'When',
  pa_headless_ret_th_what: 'What',
  pa_headless_ret_th_mode: 'Mode',
  pa_headless_ret_th_reason: 'Reason',
  pa_headless_ret_th_by: 'By',
  pa_headless_ret_none: 'Nothing has been destroyed.',
  pa_headless_agm_registered: 'Registered',
  pa_headless_agm_first_due: 'First meeting due by',
  pa_headless_agm_last_held: 'Last meeting held',
  pa_headless_agm_next_due: 'Next due by',
  pa_headless_agm_overdue: 'Overdue',
  pa_headless_wht_settled: '{{n}} settled',
  pa_headless_wht_payer_alone: 'Payer liable alone',
  pa_headless_wht_joint: 'Jointly liable',
  pa_headless_wht_at_risk: 'Tax still at risk: {{amount}} บาท.',
  pa_headless_wht_th_paid: 'Paid',
  pa_headless_wht_th_payee: 'Payee',
  pa_headless_wht_th_form: 'Form',
  pa_headless_wht_th_gross: 'Gross',
  pa_headless_wht_th_withheld: 'Withheld',
  pa_headless_wht_th_who: 'Who owes it',
  pa_headless_wht_th_why: 'Why',
  pa_headless_wht_all_clear: 'Every line was withheld and remitted.',
  pa_headless_sso_rule_a: 'A newly registered company is an employer by operation of law and registers its employees for social security. Per-employee SSO ids were already held; the ',
  pa_headless_sso_rule_b: 'employer\'s own',
  pa_headless_sso_rule_c: ' account was not.',
  pa_headless_sso_account_no: 'Employer account number',
  pa_headless_sso_branch: 'Branch',
  pa_headless_sso_registered_on: 'Registered on',
  pa_headless_sso_form_101: 'สปส.1-01',
  pa_headless_sso_office: 'Area office',
  pa_headless_sso_saved: 'Saved.',
  pa_headless_sso_save: 'Save',
  pa_headless_sso_employees: '{{n}} employees',
  pa_headless_sso_no_number: '{{n}} with no social-security number',
  pa_headless_rev_rule_a: 'Revenue Code s.65 bis (3)-(4) — an upward revaluation gain is excluded from taxable profit',
  pa_headless_rev_rule_b: ' and',
  pa_headless_rev_rule_c: ' from the depreciation base, so the book basis and the tax basis diverge permanently.',
  pa_headless_rev_th_asset: 'Asset',
  pa_headless_rev_th_cost: 'Cost',
  pa_headless_rev_th_book: 'Book basis',
  pa_headless_rev_th_tax: 'Tax basis',
  pa_headless_rev_th_reval: 'Revaluation',
  pa_headless_rev_amount_prompt: 'New carrying amount for {{name}} (baht)?',
  pa_headless_rev_date_prompt: 'Revalued on (YYYY-MM-DD)?',
  pa_headless_rev_basis_prompt: 'Who valued it, and under what report? (required)',
  pa_headless_rev_revalue: 'Revalue…',
  pa_headless_rev_empty: 'No assets on the register.',
  pa_headless_module_title: 'The rest',
  pa_headless_sec_wa_name: 'บัญชีทำการ',
  pa_headless_sec_wa: 'Working account (s.68 bis)',
  pa_headless_sec_wa_sub: 'Built, and had no button until now.',
  pa_headless_sec_fw_name: 'แม่บทการรายงานทางการเงิน',
  pa_headless_sec_fw: 'Reporting framework',
  pa_headless_sec_fw_sub: 'Now recorded against the period — a closed one is never restated.',
  pa_headless_sec_sh_name: 'ทะเบียนผู้ถือหุ้น',
  pa_headless_sec_sh: 'Share register / บอจ.5',
  pa_headless_sec_sh_sub: 'Holdings as at a date, with the transfer chain.',
  pa_headless_sec_pkg: 'DBD e-Filing package',
  pa_headless_sec_pkg_sub: 'Three slots, the unit declaration, and what cannot be produced here.',
  pa_headless_sec_cit: 'CIT treatment by account (s.65 ter)',
  pa_headless_sec_cit_sub: 'Subsections (1)–(6) only — the rest has not been obtained.',
  pa_headless_sec_fx_name: 'ใบกำกับภาษีสกุลเงินต่างประเทศ',
  pa_headless_sec_fx: 'Foreign-currency approval (s.86/4)',
  pa_headless_sec_fx_sub: '⚠️ Now enforced when a tax invoice is issued or edited.',
  pa_headless_sec_ret: 'Retention and destruction register (s.14)',
  pa_headless_sec_ret_sub: 'The machinery existed with no route at all.',
  pa_headless_sec_agm: 'General meetings (CCC s.1197)',
  pa_headless_sec_agm_sub: 'The first within 6 months of registration, then every 12.',
  pa_headless_sec_wht: 'Who owes the withholding (s.54)',
  pa_headless_sec_wht_sub: 'Withheld-not-remitted is the payer alone; never withheld is joint.',
  pa_headless_sec_sso: 'Employer social-security registration',
  pa_headless_sec_sso_sub: 'The employer\'s own account, not the employees\'.',
  pa_headless_sec_rev: 'Asset revaluation, book vs tax basis (s.65 bis)',
  pa_headless_sec_rev_sub: 'The gain is out of taxable profit and out of the depreciation base.',

  // ── Registers — the statutory registers ───────────────────────────────────────
  pa_reg_title: 'Statutory registers',
  pa_reg_record: 'Record',
  pa_reg_save: 'Save',
  pa_reg_saved: 'Saved.',
  pa_reg_empty: 'Nothing on the register.',
  pa_reg_overdue: 'overdue',
  pa_reg_days_late: '{{n}}d late',
  pa_reg_doc_directors_report: 'รายงานของกรรมการ',
  pa_reg_doc_firm_register: 'ทะเบียนสำนักงานบัญชี',
  pa_reg_doc_liquidation_report: 'รายงานการชำระบัญชี',
  pa_reg_doc_sales_tax_invoice: 'ใบกำกับภาษีขาย',
  pa_reg_incident_kind_lost: 'Lost',
  pa_reg_incident_kind_damaged: 'Damaged',
  pa_reg_incident_discovered: 'discovered',
  pa_reg_incident_happened: 'happened',
  pa_reg_incident_scope_ph: 'which books / documents',
  pa_reg_incident_cause_ph: 'cause (s.16: blank = presumed intentional)',
  pa_reg_incident_presumed_alert: 's.16: a loss with no stated cause is PRESUMED INTENTIONAL. Record what happened as soon as it is known.',
  pa_reg_incident_th_discovered: 'Discovered',
  pa_reg_incident_th_what: 'What',
  pa_reg_incident_th_scope: 'Scope',
  pa_reg_incident_th_cause: 'Cause',
  pa_reg_incident_th_notify_by: 'Notify by',
  pa_reg_incident_th_notified: 'Notified',
  pa_reg_incident_recovered: 'recovered',
  pa_reg_incident_cause_blank: 'not stated — s.16 presumes intentional',
  pa_reg_incident_notified_prompt: 'Notified the DBD on (YYYY-MM-DD)?',
  pa_reg_incident_epermit_prompt: 'e-Permit reference?',
  pa_reg_incident_notified_btn: 'Notified…',
  pa_reg_incident_unexplained: 'Incidents with no stated cause: {{n}}. s.16 presumes an unexplained loss intentional — record what happened.',
  pa_reg_cess_ceased: 'ceased',
  pa_reg_cess_reason_ph: 'reason',
  pa_reg_cess_record: 'Record cessation',
  pa_reg_cess_ceased_on: 'Ceased {{date}}',
  pa_reg_cess_deliver_by: 'Deliver by',
  pa_reg_cess_extended_to: 'extended to {{date}}',
  pa_reg_cess_max_extension: '(extendable to {{date}} at the latest)',
  pa_reg_cess_delivered: 'Delivered {{date}}',
  pa_reg_cess_not_delivered: 'Not delivered',
  pa_reg_cess_overdue_days: 'overdue by {{n}} days',
  pa_reg_cess_extend_prompt: 'Extended to (YYYY-MM-DD, at the latest {{date}})?',
  pa_reg_cess_extend_ref_prompt: 'Extension reference?',
  pa_reg_cess_extend_btn: 'Extension granted…',
  pa_reg_cess_delivered_prompt: 'Delivered to the DBD on (YYYY-MM-DD)?',
  pa_reg_cess_receipt_prompt: 'DBD\'s acknowledgement reference?',
  pa_reg_cess_delivered_btn: 'Delivered…',
  pa_reg_insp_demanded: 'demanded',
  pa_reg_insp_authority_ph: 'authority',
  pa_reg_insp_inspector_ph: 'inspector',
  pa_reg_insp_due: 'due',
  pa_reg_insp_record: 'Record demand',
  pa_reg_insp_asked_label: 'Asked to produce',
  pa_reg_insp_preview: 'Preview the manifest',
  pa_reg_insp_build: 'Build the pack',
  pa_reg_insp_th_demanded: 'Demanded',
  pa_reg_insp_th_by: 'By',
  pa_reg_insp_th_asked: 'Asked for',
  pa_reg_insp_th_due: 'Due',
  pa_reg_insp_th_answered: 'Answered',
  pa_reg_insp_answered_prompt: 'Answered on (YYYY-MM-DD)?',
  pa_reg_insp_answered_btn: 'Answered…',
  pa_reg_chg_resolved: 'resolved',
  pa_reg_chg_from_ph: 'from',
  pa_reg_chg_to_ph: 'to',
  pa_reg_chg_liquidation_due: 'the next liquidation report is due {{due}} (three months from {{since}}).',
  pa_reg_chg_th_resolved: 'Resolved',
  pa_reg_chg_th_what: 'What',
  pa_reg_chg_th_fromto: 'From → to',
  pa_reg_chg_th_register_by: 'Register by',
  pa_reg_chg_th_registered: 'Registered',
  pa_reg_chg_registered_prompt: 'Registered with the DBD on (YYYY-MM-DD)?',
  pa_reg_chg_receipt_prompt: 'Receipt number?',
  pa_reg_chg_registered_btn: 'Registered…',
  pa_reg_dr_meeting: 'meeting',
  pa_reg_dr_presented: 'presented',
  pa_reg_dr_body_th_label: 'Directors\' report (Thai)',
  pa_reg_dr_body_en_label: 'Directors\' report (English)',
  pa_reg_vat_rev12: 'Revenue, 12 months {{from}} – {{to}}',
  pa_reg_vat_source: 'from {{source}}',
  pa_reg_vat_source_ledger: 'the books',
  pa_reg_vat_exceeded_head: 'Over 1.8 ล้านบาท.',
  pa_reg_vat_exceeded_body: 'The rolling twelve months are over the registration threshold.',
  pa_reg_vat_notice_ack_by: 'Confirmed by',
  pa_reg_vat_notice_ack_tail: ', so the daily notice has stopped.',
  pa_reg_vat_notice_sending: 'Daily notice — sent to Telegram and office@slf-accounting.co, {{n}} times.',
  pa_reg_vat_notice_sending_last: 'Daily notice — sent to Telegram and office@slf-accounting.co, {{n}} times, last on {{date}}.',
  pa_reg_vat_notice_stops: 'It stops when somebody clicks the link in the email.',
  pa_reg_vat_notice_last_send: 'Last send:',
  pa_reg_vat_notice_resume: 'Start sending again',
  pa_reg_stock_cost_formula: 'cost formula',
  pa_reg_stock_method_none: '— not chosen —',
  pa_reg_stock_th_item: 'Item',
  pa_reg_stock_th_qty: 'Qty',
  pa_reg_stock_th_cost: 'Cost',
  pa_reg_stock_th_market: 'Market',
  pa_reg_stock_th_carried: 'Carried',
  pa_reg_stock_th_writedown: 'Write-down',
  pa_reg_stock_total: 'Total',
  pa_reg_stock_empty: 'Nothing on hand to value.',
  pa_reg_stock_struck_msg: 'Struck. Write-down {{amount}} on draft entry {{doc}} — review it with the other drafts.',
  pa_reg_stock_struck_none: 'Struck. Nothing to write down.',
  pa_reg_stock_strike_btn: 'Strike the valuation',
  pa_reg_stock_struck_line: 'Struck: {{n}} items, on {{date}} by {{name}}.',
  pa_reg_firm_rule: 'Accounting Professions Act s.11 — a juristic person providing accounting services registers with the TFAC and posts a guarantee for third-party liability; one offering audit services must be bound by a CPA, and is jointly liable with its auditor.',
  pa_reg_firm_regno_ph: 'TFAC registration no.',
  pa_reg_firm_registered: 'registered',
  pa_reg_firm_expires: 'expires',
  pa_reg_firm_guarantee_ph: 'guarantee posted (baht)',
  pa_reg_firm_guarantee_form_ph: 'form (bank guarantee…)',
  pa_reg_firm_offers_audit: 'offers audit services',
  pa_reg_firm_cpa_name_ph: 'CPA signatory',
  pa_reg_firm_cpa_no_ph: 'CPA licence no.',
  pa_reg_firm_expired: 'Expired — this registration expired on {{date}}.',
  pa_reg_firm_expiring: 'Expires {{date}} — within 60 days.',
  pa_reg_sec_incidents_title: 'Lost or damaged books or documents (ss.15-16)',
  pa_reg_sec_incidents_sub: 'Notified within 15 days of discovery. An unexplained loss is presumed intentional.',
  pa_reg_sec_cess_title: 'Ceasing business (s.17)',
  pa_reg_sec_cess_sub: 'The books go to the DBD within 90 days, extendable to 180.',
  pa_reg_sec_insp_title: 'Inspector\'s demand (ss.22, 24, 25)',
  pa_reg_sec_insp_sub: 'Record the demand, and build the books, vouchers and code manual as one pack.',
  pa_reg_sec_chg_title: 'Registrable changes (14 days)',
  pa_reg_sec_chg_sub: 'Capital, articles, directors; and a liquidation\'s three-month reporting cycle.',
  pa_reg_sec_dr_title_gloss: 'Directors\' report (CCC s.1198)',
  pa_reg_sec_dr_sub: 'Presented to the meeting with the balance sheet. No form is prescribed.',
  pa_reg_sec_stock_title: 'Closing stock, lower of cost or market (s.65 bis)',
  pa_reg_sec_stock_sub: 'FIFO or weighted average, locked once used. (Value in − value out is not a cost formula.)',
  pa_reg_sec_vat_title: 'VAT registration threshold 1.8 ล้าน',
  pa_reg_sec_vat_sub: 'Rolling twelve months of revenue against 1,800,000 baht.',
  pa_reg_sec_firm_title_gloss: 'The firm\'s own TFAC registration (APA s.11)',
  pa_reg_sec_firm_sub: 'About SLF, not a client: registration, guarantee, and the CPA who binds it.',

  // ── TaxLedgers — the six tax ledgers ──────────────────────────────────────────
  pa_tax_channel_label: 'Files',
  pa_tax_channel_online: 'Internet (ยื่นออนไลน์)',
  pa_tax_channel_paper: 'Paper (ยื่นกระดาษ)',
  pa_tax_channel_deadlines_paper: 'Monthly returns are due the 7th (VAT the 15th); PND.50 is 150 days, PND.51 two months — no internet extension.',
  pa_tax_channel_deadlines_online: 'Monthly returns are due the 15th (VAT the 23rd); PND.50 is 158 days, PND.51 two months and eight days.',
  pa_tax_fx_rule_1: 's.65 bis (5) — year-end balances convert at the Bank of Thailand rate, on one method applied consistently. There is no rate source in this system: rates are imported, and a balance with no rate on or before the year end is ',
  pa_tax_fx_rule_not: 'not',
  pa_tax_fx_rule_2: ' converted at anything.',
  pa_tax_fx_method_label: 'Method',
  pa_tax_fx_other_method_prompt: 'What is the approved method, and who approved it?',
  pa_tax_fx_method_none: '— not chosen —',
  pa_tax_fx_no_method_warn: 'No method is locked. s.65 bis (5) requires one method applied consistently, so nothing will be revalued until it is chosen — and it cannot be changed once a period has been revalued on it.',
  pa_tax_fx_rate_label: 'rate',
  pa_tax_fx_kind_avg: 'BOT average',
  pa_tax_fx_kind_buy: 'BOT buying',
  pa_tax_fx_kind_sell: 'BOT selling',
  pa_tax_fx_source_ph: 'where it came from',
  pa_tax_fx_rate_stored: 'Rate stored.',
  pa_tax_fx_nothing_imported: 'Nothing imported.',
  pa_tax_fx_add_rate: 'Add rate',
  pa_tax_fx_account_none: '— account —',
  pa_tax_fx_amount_fx_ph: 'amount in currency',
  pa_tax_fx_booked_ph: 'baht in the books',
  pa_tax_fx_add_balance: 'Add balance',
  pa_tax_fx_th_account: 'Account',
  pa_tax_fx_th_cur: 'Cur',
  pa_tax_fx_th_amount: 'Amount',
  pa_tax_fx_th_booked: 'In the books',
  pa_tax_fx_th_rate: 'Rate',
  pa_tax_fx_th_at_rate: 'At the rate',
  pa_tax_fx_th_diff: 'Gain / (loss)',
  pa_tax_fx_empty: 'No foreign-currency balances recorded for this period.',
  pa_tax_fx_revalued_msg: 'Draft entry {{doc}} — net {{amount}} · balances revalued: {{n}}. Review it with the other drafts before posting.',
  pa_tax_fx_revalue_btn: 'Revalue {{amount}}',
  pa_tax_rem_warn_1: 'This system holds ',
  pa_tax_rem_warn_norate: 'no rate',
  pa_tax_rem_warn_2: ' for either form, and no statutory basis for them. The domestic ท.ป.4/2528 rates are a different provision and must not be used here — every line carries the rate the adviser gives it, and the line records who entered it.',
  pa_tax_rem_lines: '{{n}} lines',
  pa_tax_rem_base: 'base {{amount}}',
  pa_tax_rem_tax: 'tax',
  pa_tax_rem_due: 'due {{date}}',
  pa_tax_rem_filed: 'filed',
  pa_tax_rem_payee_ph: 'payee',
  pa_tax_rem_country_ph: 'country',
  pa_tax_rem_nature_ph: 'what for',
  pa_tax_rem_base_ph: 'baht base',
  pa_tax_rem_rate_ph: 'rate %',
  pa_tax_rem_basis_ph: 'statutory basis',
  pa_tax_rem_add: 'Add',
  pa_tax_rem_th_paid: 'Paid',
  pa_tax_rem_th_payee: 'Payee',
  pa_tax_rem_th_for: 'For',
  pa_tax_rem_th_base: 'Base',
  pa_tax_rem_th_rate: 'Rate',
  pa_tax_rem_th_tax: 'Tax',
  pa_tax_rem_th_basis: 'Basis',
  pa_tax_rem_not_recorded: 'not recorded',
  pa_tax_rem_empty: 'Nothing on this month\'s return.',
  pa_tax_rem_filed_prompt: 'Remitted on (YYYY-MM-DD)?',
  pa_tax_rem_mark_filed: 'Mark filed',
  pa_tax_stamp_rule: 'ss.104 and 118 — an instrument in the Schedule must be fully stamped, and an unstamped one cannot be used in evidence until duty and surcharge are paid. As the contractor under its own engagement contracts the firm owes 1 baht per 1,000 — 0.1% — on them.',
  pa_tax_stamp_party_ph: 'counterparty',
  pa_tax_stamp_base_ph: 'rent / fee / principal',
  pa_tax_stamp_pick_none: '— which figure? —',
  pa_tax_baht_amount: '{{amount}} baht',
  pa_tax_stamp_one_act: ' (one act)',
  pa_tax_stamp_several_acts: ' (several acts)',
  pa_tax_stamp_vat_exempt: 'VAT on this amount (exempt)',
  pa_tax_stamp_record: 'Record',
  pa_tax_stamp_schedule_head: 'ลักษณะแห่งตราสาร',
  pa_tax_stamp_number_inferred: ' (number inferred — the source gives the seven as a set)',
  pa_tax_stamp_band: ' · the schedule gives {{min}}–{{max}} baht, a range and not a formula',
  pa_tax_stamp_th_date: 'Date',
  pa_tax_stamp_th_instrument: 'Instrument',
  pa_tax_stamp_th_party: 'Party',
  pa_tax_stamp_th_base: 'Base',
  pa_tax_stamp_th_duty: 'Duty',
  pa_tax_stamp_th_stamped: 'Stamped',
  pa_tax_stamp_not_established: 'not established',
  pa_tax_stamp_exempt: 'exempt',
  pa_tax_stamp_unpaid: 'no',
  pa_tax_stamp_method_prompt: 'Paid by adhesive, impressed or cash?',
  pa_tax_stamp_paid_on_prompt: 'Paid on (YYYY-MM-DD)?',
  pa_tax_stamp_duty_prompt: 'Duty paid (baht)?',
  pa_tax_stamp_receipt_prompt: 'อ.ส. receipt number?',
  pa_tax_stamp_mark_btn: 'Stamped',
  pa_tax_stamp_empty: 'Nothing on the register.',
  pa_tax_stamp_unstamped_count: '{{n}} unstamped.',
  pa_tax_stamp_duty_established: 'Duty established: {{amount}}',
  pa_tax_stamp_unquantified: ' · {{n}} with no duty figure yet',
  pa_tax_loss_year_ph: 'year',
  pa_tax_loss_amount_ph: 'loss (baht)',
  pa_tax_loss_note_ph: 'note',
  pa_tax_loss_record: 'Record loss',
  pa_tax_loss_years_prompt: 'Carry-forward period, in years?',
  pa_tax_loss_basis_prompt: 'Which provision does that come from? (required)',
  pa_tax_loss_set_period: 'Set the period…',
  pa_tax_loss_th_year: 'Year',
  pa_tax_loss_th_loss: 'Loss',
  pa_tax_loss_th_used: 'Used',
  pa_tax_loss_th_left: 'Left',
  pa_tax_loss_th_expires: 'Expires',
  pa_tax_loss_th_used_in: 'Used in',
  pa_tax_loss_use_year_prompt: 'Used in which year?',
  pa_tax_loss_use_amount_prompt: 'How much of it (baht)?',
  pa_tax_loss_use_btn: 'Use…',
  pa_tax_loss_total: 'Total',
  pa_tax_loss_empty: 'No losses on the register.',
  pa_tax_loss_unreconciled: 'The recorded totals and the sum of the individual uses disagree. Nothing here is safe to file until that is sorted out.',
  pa_tax_rc_no_threshold: 'No threshold is recorded, so this check will not run. s.105 gives only a ceiling — 1,000 baht for a VAT/SBT registrant, 10,000 otherwise — and the Director-General\'s notified amount is not held here. Record it, or record 0 to mean every payment, which is the safe reading while it is unknown.',
  pa_tax_rc_registrant_prompt: 'VAT/SBT registrant? type vat_sbt or other',
  pa_tax_rc_threshold_prompt: 'Threshold in baht (0 = every payment; ceiling {{ceiling}})',
  pa_tax_rc_notification_prompt: 'Director-General notification number, if the office has it',
  pa_tax_rc_change_threshold: 'Change the threshold…',
  pa_tax_rc_record_threshold: 'Record the threshold…',
  pa_tax_rc_registrant_yes: 'VAT/SBT registrant',
  pa_tax_rc_registrant_no: 'not a registrant',
  pa_tax_rc_scope_level: '{{scope}}-level',
  pa_tax_rc_th_received: 'Received',
  pa_tax_rc_th_against: 'Against',
  pa_tax_rc_th_amount: 'Amount',
  pa_tax_rc_th_desc: 'Description',
  pa_tax_rc_all_covered: 'Every matched payment in this range has a receipt behind it ({{n}} checked).',
  pa_tax_rc_daily_head: 's.105 ter daily aggregate',
  pa_tax_rc_daily_body: '{{n}} days, {{amount}} in total.',
  pa_tax_title: 'Tax ledgers',
  pa_tax_sec_fx_title: 'Exchange rates and year-end retranslation',
  pa_tax_sec_fx_sub: 's.65 bis (5) — one method, applied consistently. No rate here, no revaluation.',
  pa_tax_sec_rem_forms: 'ภ.ง.ด.54 · ภ.พ.36',
  pa_tax_sec_rem_title: 'payments abroad',
  pa_tax_sec_rem_sub: 'A register, not a rate engine: nothing here knows the rate for either form.',
  pa_tax_sec_stamp_title: 'Stamp duty',
  pa_tax_sec_stamp_sub: 'The seven schedule rates a law or accounting firm meets, and what is not established.',
  pa_tax_sec_loss_title: 'Loss carry-forward register',
  pa_tax_sec_loss_sub: 'One row per loss-making year, with no expiry claimed until the period is recorded.',
  pa_tax_sec_rc_doc: 'ใบรับ',
  pa_tax_sec_rc_title: 'Receipt issued on every payment (s.105)',
  pa_tax_sec_rc_sub: 'Money in with no receipt behind it. Refuses to run until the threshold is recorded.',
  // ── The product library against the statutory stock account (owner 2026-10-02) ───────────────
  pa_cat_sec_title: 'คลังสินค้าของลูกค้า · Product library vs the stock account',
  pa_cat_sec_sub: 'A product the client sells IS a statutory stock item. Anything in one list and not the other is a sale with no recorded cost — check this before closing the month, not after.',
  pa_cat_rule: 'The client maintains their products in their own portal; บัญชีสินค้า is the statutory book those products are recorded in. The statutory item is the master: its code and Thai name cannot be rewritten from a price list.',
  pa_cat_clean: 'The two lists agree — every item the client sells is in their stock account.',
  pa_cat_n_gaps: '{{n}} thing(s) do not match',
  pa_cat_fix: 'Fill the {{n}} gap(s) that need nothing from anyone',
  pa_cat_fixed: '{{n}} closed · {{left}} still need someone to answer them.',
  pa_cat_th_what: 'What',
  pa_cat_th_name: 'Item',
  pa_cat_th_why: 'Why it matters',
  pa_cat_kind_product_missing_particulars: 'INCOMPLETE',
  pa_cat_kind_product_unlinked: 'NOT IN THE BOOK',
  pa_cat_kind_item_without_product: 'NOT SELLABLE',
  pa_cat_kind_product_undecided: 'GOODS OR SERVICE?',
  pa_cat_kind_no_merchant_tenant: 'NO PORTAL',
  pa_cat_kind_drift: 'DISAGREE',
  // ── Stock movements from a spreadsheet (owner 2026-10-02) ───────────────────────────────────
  pa_imp_sec_title: 'นำเข้าความเคลื่อนไหวสินค้า · Import stock movements',
  pa_imp_sec_sub: 'A year of receipts and issues from the client\'s own file. You map the columns and say which of the sheet\'s words mean in and out; nothing is guessed.',
  pa_imp_rule: 'Movements feed both บัญชีสินค้า and รายงานสินค้าและวัตถุดิบ. Re-uploading the same file is safe — a movement already imported is recognised and not written twice.',
  pa_imp_read: 'Read the sheet',
  pa_imp_map_head: 'What each column is',
  pa_imp_th_col: 'Column',
  pa_imp_th_sample: 'First few values',
  pa_imp_th_is: 'Is',
  pa_imp_f_ignore: '— leave it in the sheet —',
  pa_imp_f_itemCode: 'Item code',
  pa_imp_f_itemName: 'Item name (to match)',
  pa_imp_f_itemNameTh: 'Thai name (for new items)',
  pa_imp_f_unit: 'Unit of measure',
  pa_imp_f_itemType: 'Type (ชนิด)',
  pa_imp_f_moveDate: 'Date',
  pa_imp_f_direction: 'In / out (one column)',
  pa_imp_f_quantity: 'Quantity',
  pa_imp_f_qtyIn: 'Quantity IN',
  pa_imp_f_qtyOut: 'Quantity OUT',
  pa_imp_f_value: 'Amount (receipts only)',
  pa_imp_f_docNo: 'Document no.',
  pa_imp_f_partyName: 'Supplier / buyer',
  pa_imp_f_note: 'Note',
  pa_imp_dir_head: 'What this sheet\'s words mean',
  pa_imp_dir_hint: 'A word left unset skips its rows. Read backwards, a delivery becomes a sale and the arithmetic still balances — so nothing here is assumed.',
  pa_imp_dir_unset: '— not set —',
  pa_imp_dir_in: 'In (received)',
  pa_imp_dir_out: 'Out (issued)',
  pa_imp_summary: '{{rows}} movement(s) ready · {{skipped}} row(s) skipped',
  pa_imp_create_items: 'Also add the {{n}} item(s) that are not in the stock account yet',
  pa_imp_commit: 'Import',
  pa_imp_dropped: '{{n}} issue row(s) have an amount in the money column. It is NOT imported: an issue\'s cost is derived by this client\'s elected formula, and what a sheet holds there is the selling price. Importing it would leave the valuation unchanged and make the stock value read as reduced by revenue.',
  pa_imp_uncreatable: '{{n}} row(s) name an item that is not in the stock account and do not carry the code, Thai name and unit needed to add it — they are skipped, and nothing is invented for them.',
  pa_imp_th_row: 'Row',
  pa_imp_th_item: 'Item',
  pa_imp_th_skipwhy: 'Why it was skipped',
  pa_imp_done: 'Imported {{created}} · already there {{dup}} · items added {{items}}',
  pa_imp_done_failed: '{{n}} row(s) could not be written:',
  pa_imp_row_n: 'Row {{n}}:',
  pa_imp_rerun_safe: 'Fix those rows in the file and upload it again — what is already in is recognised and not written twice.',
  // ── One movement, by hand (owner 2026-10-02) ────────────────────────────────────────────────
  pa_mv_sec_title: 'บันทึกรายการสินค้า · Record a movement by hand',
  pa_mv_sec_sub: 'For the single receipt nobody put in the file, and for correcting what went in wrong.',
  pa_mv_rule: 'Hand-entered movements sit in บัญชีสินค้า beside the imported ones and the ones derived from tax invoices. ⛔ A movement is corrected by REVERSING it, never by deleting it — the line is in a statutory book, so the original stays and an opposite movement says what actually happened.',
  pa_mv_no_items: 'This client has no stock items yet. Add them in the client\'s product library, or let a spreadsheet import create them.',
  pa_mv_f_item: 'Item',
  pa_mv_pick_item: '— pick an item —',
  pa_mv_on_hand: '{{n}} {{unit}} on hand',
  pa_mv_f_date: 'Date it moved',
  pa_mv_f_direction: 'In or out',
  pa_mv_f_qty: 'Quantity',
  pa_mv_f_value: 'Cost (บาท)',
  pa_mv_no_value_on_out: 'No cost is entered on an issue. Its cost is worked out by this client\'s elected formula (FIFO or weighted average) — a figure typed here would change nothing in the valuation and would make the stock value read as reduced by revenue.',
  pa_mv_add: 'Record it',
  pa_mv_saved: 'Recorded.',
  pa_mv_backdated: 'This is back-dated. s.87(3) allows three working days from the movement to the write-up, and the report shows how long each one took.',
  pa_mv_th_move: 'Move',
  pa_mv_th_from: 'From',
  pa_mv_src_manual: 'By hand',
  pa_mv_src_import: 'Spreadsheet',
  pa_mv_src_invoice: 'Tax invoice',
  pa_mv_src_reversal: 'A reversal',
  pa_mv_reverse: 'Reverse',
  pa_mv_reverse_confirm: 'Reverse this movement of {{qty}} × {{name}}? The original stays in the book and an opposite movement is added, dated today. This cannot be undone — reverse the reversal if you need to.',
  pa_mv_reversed: 'Reversed. The original stays in the book, with the correction beside it.',
  pa_mv_already_reversed: 'That movement had already been reversed — nothing was added.',
  pa_mv_is_reversed: 'reversed',
};

export const acctZh: Record<string, string> = {
  pa_sb_period_label: '会计期间',
  pa_sb_open_books_gloss: '开立会计期间',
  pa_sb_open_books_why: '会计法要求每十二个月结账一次,因此必须先有会计期间才能过账。按常规十二个月办理时,结束日期留空;第一个期间可以短于十二个月,此后任何不足或超过十二个月的期间,都须先登记会计监督官的书面许可。开立时会把事务所的会计科目表一并复制进来。',
  pa_sb_from: '开始',
  pa_sb_to: '结束',
  pa_sb_optional: '可不填',
  pa_sb_open_period: '开立期间',
  pa_sb_open_next: '开立下一期间',
  pa_sb_status_closed: '已结账',
  pa_sb_n_entries_other: '{{count}} 笔分录',
  pa_sb_nothing_posted: '本期间还没有任何过账 —— 先在上方生成待过账分录,核对后过账。',

  pa_sb_close_gloss: '结账…',
  pa_sb_close_confirm: '结账不可撤销。本期损益将转入未分配利润,结转余额随之固定,并对本期间的凭证启动五年保存期限。确定继续?',
  pa_sb_close_sum: '收入 {{income}} − 费用 {{expense}} =',
  pa_sb_profit: '利润',
  pa_sb_loss: '亏损',
  pa_sb_close_drafts_other: '还有 {{count}} 笔待过账分录 —— 请先处理完。',
  pa_sb_close_outofrange_other: '有 {{count}} 笔已过账分录落在本期间之外。',
  pa_sb_close_unbalanced: '本期间借贷不平。',
  pa_sb_close_it: '确认结账',
  pa_sb_closed_detail: '{{entries}} 笔分录 · {{result}} {{amount}} · 结转余额 {{carried}} 项 · {{vouchers}} 张凭证已标记五年保存。',

  pa_sb_h_journals: '日记账',
  pa_sb_h_ledger: '总账与试算表',
  pa_sb_h_statements: '财务报表',
  pa_sb_h_extras: '现金流量表、附注、存货与增值税',

  pa_sb_c_gl: '总账',
  pa_sb_c_tb: '试算表',
  pa_sb_c_ar: '应收明细账',
  pa_sb_c_ap: '应付明细账',
  pa_sb_c_stock: '存货账 —— 仅商品类客户',
  pa_sb_c_goods: '商品及原材料报表',
  pa_sb_c_vatout: '销项税报表 —— 本月',
  pa_sb_c_vatin: '进项税报表 —— 本月',

  pa_sb_pl_line: '收入 {{income}} − 费用 {{expense}}',
  pa_sb_assets_line: '资产 {{amount}}',
  pa_sb_unbalanced_why: '资产不等于负债加所有者权益',

  pa_fs_bound_what: '财务报表合订本:封面、带页码的目录、连续页码',
  pa_fs_bound_build: '生成合订本',
  pa_fs_bound_building: '正在装订…',
  pa_fs_cover_issues: '将印在封面上',
  pa_fs_pick_least: '请至少选择一张报表。',
  pa_fs_sfp: '资产负债表',
  pa_fs_pl: '损益表',
  pa_fs_sce: '所有者权益变动表',
  pa_fs_cf: '现金流量表',
  pa_fs_notes: '财务报表附注',
  pa_fs_wa: '营业账 —— 随 ภ.ง.ด.50 一并报送',

  pa_sb_foot_books: '账簿以泰文打印,封面载明 {{client}}、账簿种类及册次,并连续编页 —— 即商业发展厅 2544 年公告所规定的格式。',
  pa_sb_foot_statements: '报表依权责发生制自上述账簿编制。尚未按商业发展厅 2566 年公告规定整理为简式项目(รายการย่อ),因此不是向商业发展厅报送的格式 —— 该对应关系有待公告正式条文。',

  // ── AccountingBooks — the client picker ───────────────────────────────────────
  pa_books_all_clients: '全部客户',
  pa_books_title: '法定账簿',
  pa_books_intro: '会计科目表、会计期间、五本日记账、总账、年度结账和财务报表。所有记账客户都会列出 — 包括尚无发票的客户，因为新设立的公司正是最需要开立会计期间的那一家。',
  pa_books_search_ph: '搜索客户…',
  pa_books_empty: '没有匹配的记账客户。',
  pa_books_drafts_pill: '{{n}} 草稿',
  pa_books_no_period: '尚未开账',
  pa_books_pill_closed: '已结账',
  pa_books_pill_open: '未结账',
  pa_books_card_counts: '{{accounts}} 个会计科目 · {{entries}} 笔已过账分录',
  pa_books_card_vat: '增值税',

  // ── Bookkeeping — the invoice-line-item books ─────────────────────────────────
  pa_bk_mon_jan: '1月',
  pa_bk_mon_feb: '2月',
  pa_bk_mon_mar: '3月',
  pa_bk_mon_apr: '4月',
  pa_bk_mon_may: '5月',
  pa_bk_mon_jun: '6月',
  pa_bk_mon_jul: '7月',
  pa_bk_mon_aug: '8月',
  pa_bk_mon_sep: '9月',
  pa_bk_mon_oct: '10月',
  pa_bk_mon_nov: '11月',
  pa_bk_mon_dec: '12月',
  pa_bk_individual: '自然人',
  pa_bk_item_remove: '删除',
  pa_bk_zero_rate: '0%',
  pa_bk_loading: '加载中…',
  pa_bk_books_link: '法定账簿',
  pa_bk_books_link_title: '会计科目表、日记账、总账、结账与财务报表',
  pa_bk_err_items_load: '无法加载明细',
  pa_bk_err_not_saved: '未保存',
  pa_bk_err_add: '无法添加',
  pa_bk_err_delete: '无法删除',
  pa_bk_err_clear: '无法清空明细',
  pa_bk_err_regen: '重新识别失败',
  pa_bk_err_month_load: '无法加载本月',
  pa_bk_err_photo: '无法加载图片',

  // ── EntityInfo — juristic form and dividends ──────────────────────────────────
  pa_entity_form_not_recorded: '法人形式未记录',
  pa_entity_unknown_why: '本客户是否依 s.8 有记账义务、其财务报表适用哪一种 DBD 表单、以及能否免予审计,全都取决于这一项。',
  pa_entity_must_keep: '有记账义务 (ม.8)',
  pa_entity_not_required: '非记账义务人',
  pa_entity_dbd_form: 'DBD แบบ {{n}}',
  pa_entity_reg_capital_val: '注册资本 {{amount}}',
  pa_entity_registered_on_val: '注册日 {{date}}',
  pa_entity_dividend: '股利',
  pa_entity_f_form: '法人形式',
  pa_entity_f_form_select: '— 请选择 —',
  pa_entity_form_dbd_suffix: '(แบบ {{n}})',
  pa_entity_f_capital: '注册资本 (บาท)',
  pa_entity_f_registered_on: '注册日期',
  pa_entity_div_law: 'ประมวลกฎหมายแพ่งและพาณิชย์ ม.1201–1202 — 股利只能从利润中支付,存在累计亏损时一律不得支付;并须将其中至少 5% 拨入法定公积金,直至该公积金达到注册资本的十分之一。股利须自决议之日起一个月内付清。',
  pa_entity_div_amount: '金额 (บาท)',
  pa_entity_div_resolution_date: '决议日期',
  pa_entity_div_payable_account: '应付股利科目',
  pa_entity_div_retained: '未分配利润 {{amount}}',
  pa_entity_div_reserve: '法定公积金 {{amount}}',
  pa_entity_div_ceiling: '上限 {{amount}}',
  pa_entity_div_appropriation: '拨入法定公积金 {{amount}}',
  pa_entity_div_create: '创建草稿',
  pa_entity_div_created: '{{doc}} 已创建为草稿。',
  pa_entity_div_created_appr: '{{doc}} 已创建为草稿,其中 {{amount}} 拨入法定公积金。',

  // ── DraftEntries — generate, review, post ─────────────────────────────────────
  pa_jnl_cash: 'เงินสด',
  pa_jnl_bank: 'ธนาคาร',
  pa_jnl_purchase: 'รายวันซื้อ',
  pa_jnl_sale: 'รายวันขาย',
  pa_jnl_general: 'รายวันทั่วไป',
  pa_draft_title: '待过账分录',
  pa_draft_sub_pending: '在你过账之前,这里的内容都不在账簿里。',
  pa_draft_sub_empty: '来自增值税明细账 — 已记录的销项与进项单据。',
  pa_draft_generate: '从明细账生成',
  pa_draft_hide: '收起',
  pa_draft_review: '查看核对',
  pa_draft_post_all: '全部过账 {{n}} 笔',
  pa_draft_post: '过账',
  pa_draft_discard_title: '丢弃这条待过账分录',
  pa_draft_discard_confirm: '丢弃 {{docNo}} 的待过账分录?明细账那一行会保留 — 可以再生成一次。',
  pa_draft_gen_created: '已生成待过账分录 {{n}} 笔',
  pa_draft_gen_skipped: '已有待过账分录 {{n}} 笔',
  pa_draft_gen_needs_account: '需选择费用科目 {{n}} 笔',
  pa_draft_gen_blocked: '拒绝生成 {{n}} 行 — {{list}}',
  pa_draft_gen_blocked_more: ' …另有 {{n}} 条',
  pa_draft_no_doc_no: '(无编号)',
  pa_draft_posted_single: '已过账。',
  pa_draft_posted_n: '已过账 {{n}} 笔',

  // ── ChartOfAccounts ───────────────────────────────────────────────────────────
  pa_chart_title: '会计科目表',
  pa_chart_counts: '{{n}} 个科目 · {{used}} 个已使用',
  pa_chart_empty: '尚无科目 — 先复制事务所模板。',
  pa_chart_copy_template: '复制事务所模板',
  pa_chart_sync_template: '从模板同步',
  pa_chart_sync_title: '把这家客户还没有的模板科目补齐',
  pa_chart_add_account: '添加科目',
  pa_chart_open: '展开',
  pa_chart_hide: '收起',
  pa_chart_ph_code: '科目代码',
  pa_chart_ph_name_th: '科目名称(泰文)',
  pa_chart_ph_name_en: '科目名称(英文)',
  pa_chart_save: '保存',
  pa_chart_cancel: '取消',
  pa_chart_rename: '改名',
  pa_chart_switch_off: '停用',
  pa_chart_switch_on: '启用',
  pa_chart_name_th_manual: '泰文名称即 ม.21(1) 所要求的科目代码对照手册 — 必填',
  pa_chart_template_complete: '事务所模板里的科目都已经在这本科目表里了。',
  pa_chart_remove_confirm: '删除科目 {{code}} {{name}}?',
  pa_chart_class_asset: '资产',
  pa_chart_class_liability: '负债',
  pa_chart_class_equity: '所有者权益',
  pa_chart_class_income: '收入',
  pa_chart_class_expense: '费用',
  pa_chart_cf_prefixed: 'งบกระแสเงินสด: {{section}}',
  pa_chart_cf_none: '— 未选择 —',
  pa_chart_cf_operating: '经营活动',
  pa_chart_cf_investing: '投资活动',
  pa_chart_cf_financing: '筹资活动',
  pa_chart_cf_cash: '现金及现金等价物',
  pa_chart_cf_noncash: '非现金项目',
  pa_chart_cf_missing_warn: '⚠️ 资产/负债/权益科目若不选现金流分类,现金流量表会对不平(差额正好等于走过这个科目的金额)。',
  pa_chart_cf_missing_title: '现金流量表会对不平,直到这个科目选了分类',
  pa_chart_cf_missing_pill: '未选 งบกระแสเงินสด 分类',

  // ── CompliancePanel — Accounting Act records ──────────────────────────────────
  pa_compliance_title: '审计与 DBD 报送',
  pa_compliance_file_by: '报送截止日 {{date}}',
  pa_compliance_due_unknown: '报送截止日尚未确定',
  pa_compliance_record_meeting_audit: '登记股东大会与审计',
  pa_compliance_record_filing: '登记报送',
  pa_compliance_audit_unknown: '⚠ 是否必须审计尚未确定',
  pa_compliance_audit_required: '必须由注册会计师 (CPA) 审计',
  pa_compliance_audit_exempt: '获得审计豁免',
  pa_compliance_unbacked: '{{n}} 笔分录没有凭证、没有编号、也没有说明依据 — 不能过账 (ข้อ 5)。',
  pa_compliance_filed_msg: '已登记 {{kind}} 于 {{date}} 报送。',
  pa_compliance_agm_due: '股东大会截止日 {{date}}',
  pa_compliance_agm_held: '已于 {{date}} 召开',
  pa_compliance_agm_not_held: '尚未召开',
  pa_compliance_statements_due: '{{date}} 前将财务报表送达股东',
  pa_compliance_bojo5_due: '{{date}} 前报送 บอจ.5',
  pa_compliance_filed_label: '已报送:',
  pa_compliance_bookkeeper_label: '记账人:',
  pa_compliance_tfac: '(TFAC {{no}})',
  pa_compliance_tfac_lapsed: '会员资格已失效',
  pa_compliance_no_bookkeeper: '尚未委任记账人 (Accounting Act s.19)',
  pa_compliance_eacct_pending: '尚未在 e-Accountant 申报 {{name}}(自 {{date}} 起 30 天内)',
  pa_compliance_notified_today: '今日已申报',
  pa_compliance_f_agm_date: '股东大会日期',
  pa_compliance_f_statements_sent: '财务报表送达股东',
  pa_compliance_f_audit_completed: '审计完成',
  pa_compliance_f_opinion: '审计意见',
  pa_compliance_opinion_unqualified: '无保留意见',
  pa_compliance_opinion_qualified: '保留意见',
  pa_compliance_opinion_adverse: '否定意见',
  pa_compliance_opinion_disclaimer: '无法表示意见',
  pa_compliance_f_auditor: '审计师',
  pa_compliance_f_auditor_license: '注册编号',
  pa_compliance_f_auditor_kind: '类别',
  pa_compliance_auditor_cpa: '注册会计师 (CPA)',
  pa_compliance_auditor_ta: '税务审计师 (TA)',
  pa_compliance_f_audit_fee: '审计费',
  pa_compliance_save: '保存',
  pa_compliance_f_kind: '报送内容',
  pa_compliance_kind_fs: 'งบการเงิน',
  pa_compliance_kind_fs_gloss: '财务报表',
  pa_compliance_kind_bojo5: 'บอจ.5',
  pa_compliance_kind_bojo5_gloss: '股东名册',
  pa_compliance_f_filed_on: '报送日期',
  pa_compliance_f_receipt_no: 'เลขที่ใบรับ · 收据编号',
  pa_compliance_record: '登记',

  // ── FixedAssets — the asset register ──────────────────────────────────────────
  pa_assets_register_name: 'ทะเบียนทรัพย์สิน',
  pa_assets_title: '固定资产',
  pa_assets_summary: '{{n}} 项固定资产 · 账面净值 {{nbv}}',
  pa_assets_empty: '登记簿是空的。',
  pa_assets_due: '本期应计提折旧 {{amount}}',
  pa_assets_open_register: '登记簿',
  pa_assets_charge_dep: '计提折旧 {{amount}}',
  pa_assets_add: '固定资产',
  pa_assets_open: '展开',
  pa_assets_hide: '收起',
  pa_assets_registered_msg: '{{no}} 已登记,折旧率每年 {{rate}}%。',
  pa_assets_dep_draft: '{{n}} 项固定资产,{{amount}} — 已生成待过账分录供核对。',
  pa_assets_dep_none: '本期所有固定资产都已计提折旧。',
  pa_assets_f_class: '类别',
  pa_assets_no_dep: '不计提折旧',
  pa_assets_f_acquired: '取得日期',
  pa_assets_f_cost: '原值',
  pa_assets_f_salvage: '残值',
  pa_assets_f_rate: '折旧率 %',
  pa_assets_f_name_th: '名称(泰文)— 必填',
  pa_assets_f_name_en: '名称(英文)',
  pa_assets_f_acct_asset: '资产科目',
  pa_assets_f_acct_accum: '累计折旧',
  pa_assets_f_acct_expense: '折旧费用',
  pa_assets_max_rate_source: '最高折旧率依据 {{source}}',
  pa_assets_cancel: '取消',
  pa_assets_submit: '登记',
  pa_assets_th_no: '编号',
  pa_assets_th_item: '名称',
  pa_assets_th_acquired: '取得',
  pa_assets_th_cost: '原值',
  pa_assets_th_accum: '累计折旧',
  pa_assets_th_nbv: '账面净值',
  pa_assets_th_year: '本年',
  pa_assets_disposed_on: '已处置 {{date}}',
  pa_assets_days: '{{n}} 天',

  // ── ManualEntry — the general journal ─────────────────────────────────────────
  pa_manual_book_general_gloss: '普通',
  pa_manual_book_cash_gloss: '现金',
  pa_manual_book_bank_gloss: '银行',
  pa_manual_book_purchase_gloss: '采购',
  pa_manual_book_sale_gloss: '销售',
  pa_manual_title: '调整与更正',
  pa_manual_subtitle: '应收应付预提、预付费用、折旧与各项准备 — 以及冲销,这是更正已过账分录的唯一方式。',
  pa_manual_new_btn: '登记分录',
  pa_manual_reverse_btn: '冲销',
  pa_manual_sweep_title: '将更早会计期间中应在本期冲销的应收应付预提带入本期',
  pa_manual_accruals_btn: '应收应付预提',
  pa_manual_late_entries: '晚于分录日期 15 天以上才过账的分录:{{n}} 笔 — 最晚的一笔迟 {{days}} 天({{docNo}})。',
  pa_manual_late_rule: 'DBD 要求日记账须在交易发生后 15 天内记录完毕。',
  pa_manual_closing_overdue: '本会计期间已于 {{date}} 结束,距今 {{days}} 天,仍未结账。账目须在 60 天内结账。',
  pa_manual_saved: '待过账分录 {{docNo}} 已生成 — 请在下方核对后过账。',
  pa_manual_reverse_confirm: '冲销 {{docNo}}?原分录仍留在账簿中并保留其页码;更正分录将作为待过账分录在其旁边生成。',
  pa_manual_reversed_msg: '{{docNo}} 已作为待过账分录生成,日期 {{date}}。',
  pa_manual_sweep_done: '已生成 {{n}} 笔应收应付预提的冲销分录(待过账)。',
  pa_manual_sweep_none: '没有更早会计期间的应收应付预提需要冲销到本期。',
  pa_manual_f_date: '日期',
  pa_manual_f_book: '日记账',
  pa_manual_f_bank_acct: '银行账号',
  pa_manual_f_bank_acct_hint: '一个账号一本账',
  pa_manual_f_bank_acct_ph: '例如 230-3-57579-3',
  pa_manual_bank_acct_new: '另一个账号…',
  pa_manual_bank_acct_used: '已有 {{n}} 笔',
  pa_manual_bank_acct_none: '这个客户还没有银行账簿 —— 输入账号，下次就会出现在列表里。',
  pa_manual_f_docno: '单据编号',
  pa_manual_f_docno_hint: '(留空 → 自动分配 JV 编号)',
  pa_manual_f_docno_ph: 'JV-… 自动分配',
  pa_manual_f_reverse_on: '冲销日期',
  pa_manual_f_reverse_on_hint: '(应收应付预提)',
  pa_manual_narration_ph: '摘要 — 必填',
  pa_manual_calcnote_ph: '计算方法 — 没有原始凭证时必填(ข้อ 4)',
  pa_manual_th_account: '科目',
  pa_manual_th_dr: '借方',
  pa_manual_th_cr: '贷方',
  pa_manual_pick_account: '— 选择科目 —',
  pa_manual_add_line: '分录行',
  pa_manual_no_amounts: '尚未输入金额',
  pa_manual_balances: '借贷平衡',
  pa_manual_out_by: '差额 {{amount}}',
  pa_manual_save_draft: '存为草稿',
  pa_manual_reverse_note: '已过账分录不会被撤回过账。它保留原有位置与页码,更正是记在其旁边的一笔新分录 — 这正是审计轨迹的由来。',
  pa_manual_search_ph: '搜索已过账分录…',
  pa_manual_none_posted: '没有匹配的已过账分录。',
  pa_manual_reversed_by: '已冲销 {{docNo}}',

  // ── Headless — the records with no other home ─────────────────────────────────
  pa_headless_wa_rule_a: 'Revenue Code s.68 bis — 为税务目的,每家公司都要编制资产负债表、',
  pa_headless_wa_rule_b: '损益计算表',
  pa_headless_wa_rule_c: '和利润表。⚠️ 本系统未持有 DBD 的法定格式,因此按本系统自有格式打印。',
  pa_headless_wa_income: '收入',
  pa_headless_wa_cost: '销售成本',
  pa_headless_wa_gross: '毛利',
  pa_headless_wa_expenses: '费用',
  pa_headless_wa_net: '净利润(亏损)',
  pa_headless_wa_mismatch_name: 'งบทดลอง',
  pa_headless_wa_mismatch: '与试算表不符。',
  pa_headless_wa_doc_name: 'บัญชีทำการ',
  pa_headless_wa_doc: '损益计算表',
  pa_headless_fw_rule: '表 1、2、4、5 以 TFRS for NPAEs 为基础;表 3 以 TFRS for PAEs 为基础。NPAE 若完整适用 TFRS for PAEs,可以选用,并须在财务报表附注中披露格式的变更。',
  pa_headless_fw_period: '记录在本会计期间上',
  pa_headless_fw_period_tail: ' — 已结账的期间永不重述。',
  pa_headless_fw_none: '— 未记录 —',
  pa_headless_fw_npae: 'TFRS สำหรับ NPAEs',
  pa_headless_fw_pae: 'TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ',
  pa_headless_fw_dbd_form: 'DBD แบบ {{form}}',
  pa_headless_fw_locked: '已结账 — 已锁定',
  pa_headless_sh_rule: 'CCC — 公司自设立起即须备置股东名簿,并须按股东大会当日的状况在 14 天内报送 บอจ.5。⚠️ 本系统未持有登记官的 บอจ.5 空白表格,因此名簿可供查阅核对,但无法套印到法定表格上。',
  pa_headless_sh_as_at: '截至日期',
  pa_headless_sh_holders: '{{n}} 名股东',
  pa_headless_sh_shares: '{{n}} 股',
  pa_headless_sh_form_name: 'บอจ.5',
  pa_headless_sh_form_not_held: '表格未持有',
  pa_headless_sh_name_ph: '股东姓名',
  pa_headless_sh_individual: '自然人',
  pa_headless_sh_juristic: '法人',
  pa_headless_sh_idno: '身份/统一编号',
  pa_headless_sh_nationality: '国籍',
  pa_headless_sh_shares_ph: '股数',
  pa_headless_sh_paid_per_share: '每股已缴',
  pa_headless_sh_held_from: '持有起始日',
  pa_headless_sh_add: '添加',
  pa_headless_sh_th_name: '姓名',
  pa_headless_sh_th_kind: '类别',
  pa_headless_sh_th_shares: '股数',
  pa_headless_sh_end_prompt: '持有终止日 (YYYY-MM-DD)?',
  pa_headless_sh_end: '终止…',
  pa_headless_sh_empty: '该日期尚无持股记录。',
  pa_headless_pkg_rule: '财务报表与审计师报告通过 DBD e-Filing 报送:报表以 XBRL 格式,审计师报告为黑白扫描件,另附股东名单。',
  pa_headless_pkg_unit: '单位',
  pa_headless_pkg_file: '文件 {{id}}',
  pa_headless_pkg_declared: '已声明',
  pa_headless_pkg_not_attached: '未附上',
  pa_headless_pkg_not_producible: '本系统无法生成',
  pa_headless_pkg_file_prompt: '{{slot}} 的文件 ID?',
  pa_headless_pkg_attach: '附加…',
  pa_headless_pkg_declare_bw: '声明审计师报告为黑白件',
  pa_headless_cit_rule: 'Revenue Code s.65 ter — 哪些科目在企业所得税上可以扣除、哪些不可以。⚠️ 本系统仅持有第 (1)–(6) 项;s.65 ter 其余各项尚未取得。',
  pa_headless_cit_th_account: '会计科目',
  pa_headless_cit_th_movement: '发生额',
  pa_headless_cit_th_treatment: '企业所得税处理',
  pa_headless_cit_th_note: '备注',
  pa_headless_cit_undecided: '— 尚未判定 —',
  pa_headless_cit_yes: '可扣除',
  pa_headless_cit_no: '不可扣除',
  pa_headless_cit_limited: '部分可扣除',
  pa_headless_cit_empty: '本期无需复核的科目。',
  pa_headless_fx_rule_a: 'Revenue Code s.86/4 — 税务发票以泰铢开立,除非税务局批准使用其他币种。',
  pa_headless_fx_rule_b: '开立或编辑税务发票时现已强制校验此闸门',
  pa_headless_fx_rule_c: ':在此处录入批准之前,非 THB 的税务发票一律被拒。',
  pa_headless_fx_approval_no: '税务局批准编号',
  pa_headless_fx_approved_on: '批准日',
  pa_headless_fx_effective_from: '生效日',
  pa_headless_fx_effective_to: '至',
  pa_headless_fx_recorded: '{{currency}} 的批准已登记。',
  pa_headless_fx_record: '登记批准',
  pa_headless_fx_check: '查询 {{currency}} 今日是否获批',
  pa_headless_fx_allowed: '已获批准。',
  pa_headless_fx_not_allowed: '未获批准。',
  pa_headless_ret_label: '保存期限',
  pa_headless_ret_5y: '5 年(มาตรา 14 规定的下限)',
  pa_headless_ret_6y: '6 年',
  pa_headless_ret_7y: '7 年(DBD 可延长的上限)',
  pa_headless_ret_th_when: '时间',
  pa_headless_ret_th_what: '对象',
  pa_headless_ret_th_mode: '方式',
  pa_headless_ret_th_reason: '原因',
  pa_headless_ret_th_by: '经办人',
  pa_headless_ret_none: '尚无文件被销毁。',
  pa_headless_agm_registered: '注册日',
  pa_headless_agm_first_due: '首次会议截止日',
  pa_headless_agm_last_held: '最近一次会议',
  pa_headless_agm_next_due: '下次截止日',
  pa_headless_agm_overdue: '逾期',
  pa_headless_wht_settled: '{{n}} 笔已结清',
  pa_headless_wht_payer_alone: '付款方单独承担',
  pa_headless_wht_joint: '连带责任',
  pa_headless_wht_at_risk: '尚有风险的税款合计 {{amount}} บาท。',
  pa_headless_wht_th_paid: '付款日',
  pa_headless_wht_th_payee: '收款方',
  pa_headless_wht_th_form: '表单',
  pa_headless_wht_th_gross: '支付总额',
  pa_headless_wht_th_withheld: '扣缴',
  pa_headless_wht_th_who: '谁承担',
  pa_headless_wht_th_why: '原因',
  pa_headless_wht_all_clear: '每一行都已扣缴并解缴。',
  pa_headless_sso_rule_a: '新注册的公司依法即为雇主,须为员工办理社会保险登记。每位员工的社保号本系统原已持有,唯独',
  pa_headless_sso_rule_b: '雇主自身',
  pa_headless_sso_rule_c: '的账号此前没有。',
  pa_headless_sso_account_no: '雇主账号',
  pa_headless_sso_branch: '分支',
  pa_headless_sso_registered_on: '登记日',
  pa_headless_sso_form_101: 'สปส.1-01',
  pa_headless_sso_office: '辖区办事处',
  pa_headless_sso_saved: '已保存。',
  pa_headless_sso_save: '保存',
  pa_headless_sso_employees: '{{n}} 名员工',
  pa_headless_sso_no_number: '{{n}} 人尚无社保号',
  pa_headless_rev_rule_a: 'Revenue Code s.65 bis (3)-(4) — 资产向上重估产生的增值既不计入应税利润,',
  pa_headless_rev_rule_b: '也',
  pa_headless_rev_rule_c: '不计入折旧基数,因此账面基数与税务基数将永久分离。',
  pa_headless_rev_th_asset: '资产',
  pa_headless_rev_th_cost: '原值',
  pa_headless_rev_th_book: '账面基数',
  pa_headless_rev_th_tax: '税务基数',
  pa_headless_rev_th_reval: '资产重估',
  pa_headless_rev_amount_prompt: '{{name}} 的新账面金额 (baht)?',
  pa_headless_rev_date_prompt: '重估日 (YYYY-MM-DD)?',
  pa_headless_rev_basis_prompt: '由谁评估、依据哪份报告?(必填)',
  pa_headless_rev_revalue: '重估…',
  pa_headless_rev_empty: '登记簿上没有资产。',
  pa_headless_module_title: '其余事项',
  pa_headless_sec_wa_name: 'บัญชีทำการ',
  pa_headless_sec_wa: '损益计算表(s.68 bis)',
  pa_headless_sec_wa_sub: '早已建好,此前一直没有入口按钮。',
  pa_headless_sec_fw_name: 'แม่บทการรายงานทางการเงิน',
  pa_headless_sec_fw: '财务报告框架',
  pa_headless_sec_fw_sub: '现记录在会计期间上 — 已结账的期间永不重述。',
  pa_headless_sec_sh_name: 'ทะเบียนผู้ถือหุ้น',
  pa_headless_sec_sh: '股东名簿 / บอจ.5',
  pa_headless_sec_sh_sub: '按指定日期的持股情况,含转让链条。',
  pa_headless_sec_pkg: 'DBD e-Filing 电子报送材料包',
  pa_headless_sec_pkg_sub: '三个附件位、单位声明,以及本系统无法生成的部分。',
  pa_headless_sec_cit: '按科目的企业所得税处理(s.65 ter)',
  pa_headless_sec_cit_sub: '仅第 (1)–(6) 项 — 其余尚未取得。',
  pa_headless_sec_fx_name: 'ใบกำกับภาษีสกุลเงินต่างประเทศ',
  pa_headless_sec_fx: '外币开票批准(s.86/4)',
  pa_headless_sec_fx_sub: '⚠️ 开立或编辑税务发票时现已强制校验。',
  pa_headless_sec_ret: '保存期限与销毁登记簿(s.14)',
  pa_headless_sec_ret_sub: '底层机制早已存在,却完全没有接口路由。',
  pa_headless_sec_agm: '股东大会(CCC s.1197)',
  pa_headless_sec_agm_sub: '首次须在注册后 6 个月内召开,其后每 12 个月一次。',
  pa_headless_sec_wht: '扣缴义务由谁承担(s.54)',
  pa_headless_sec_wht_sub: '已扣未解缴由付款方单独承担;自始未扣则为连带责任。',
  pa_headless_sec_sso: '雇主社保登记',
  pa_headless_sec_sso_sub: '雇主自身的账号,而非员工的。',
  pa_headless_sec_rev: '资产重估:账面基数与税务基数(s.65 bis)',
  pa_headless_sec_rev_sub: '该增值既不计入应税利润,也不计入折旧基数。',

  // ── Registers — the statutory registers ───────────────────────────────────────
  pa_reg_title: '法定登记簿',
  pa_reg_record: '登记',
  pa_reg_save: '保存',
  pa_reg_saved: '已保存。',
  pa_reg_empty: '登记簿上还没有记录。',
  pa_reg_overdue: '逾期',
  pa_reg_days_late: '逾期 {{n}} 天',
  pa_reg_doc_directors_report: 'รายงานของกรรมการ',
  pa_reg_doc_firm_register: 'ทะเบียนสำนักงานบัญชี',
  pa_reg_doc_liquidation_report: 'รายงานการชำระบัญชี',
  pa_reg_doc_sales_tax_invoice: 'ใบกำกับภาษีขาย',
  pa_reg_incident_kind_lost: '灭失',
  pa_reg_incident_kind_damaged: '损毁',
  pa_reg_incident_discovered: '发现于',
  pa_reg_incident_happened: '发生于',
  pa_reg_incident_scope_ph: '哪些账簿 / 凭证',
  pa_reg_incident_cause_ph: '原因(s.16:留空 = 推定为故意)',
  pa_reg_incident_presumed_alert: 's.16:未说明原因的灭失,一律推定为故意。实情一旦查明,请立即记录。',
  pa_reg_incident_th_discovered: '发现日',
  pa_reg_incident_th_what: '情形',
  pa_reg_incident_th_scope: '范围',
  pa_reg_incident_th_cause: '原因',
  pa_reg_incident_th_notify_by: '通知截止日',
  pa_reg_incident_th_notified: '已通知',
  pa_reg_incident_recovered: '已找回',
  pa_reg_incident_cause_blank: '未说明 — s.16 推定为故意',
  pa_reg_incident_notified_prompt: '向 DBD 通知的日期(YYYY-MM-DD)?',
  pa_reg_incident_epermit_prompt: 'e-Permit 参考号?',
  pa_reg_incident_notified_btn: '已通知…',
  pa_reg_incident_unexplained: '未说明原因的事件:{{n}} 件。s.16 对原因不明的灭失推定为故意 — 请记录实情。',
  pa_reg_cess_ceased: '停业于',
  pa_reg_cess_reason_ph: '原因',
  pa_reg_cess_record: '登记停业',
  pa_reg_cess_ceased_on: '停业 {{date}}',
  pa_reg_cess_deliver_by: '移交截止',
  pa_reg_cess_extended_to: '已延至 {{date}}',
  pa_reg_cess_max_extension: '(最迟可延至 {{date}})',
  pa_reg_cess_delivered: '已移交 {{date}}',
  pa_reg_cess_not_delivered: '尚未移交',
  pa_reg_cess_overdue_days: '逾期 {{n}} 天',
  pa_reg_cess_extend_prompt: '延至(YYYY-MM-DD,最迟 {{date}})?',
  pa_reg_cess_extend_ref_prompt: '延期批文参考号?',
  pa_reg_cess_extend_btn: '已获延期…',
  pa_reg_cess_delivered_prompt: '移交 DBD 的日期(YYYY-MM-DD)?',
  pa_reg_cess_receipt_prompt: 'DBD 的受理回执参考号?',
  pa_reg_cess_delivered_btn: '已移交…',
  pa_reg_insp_demanded: '调阅日',
  pa_reg_insp_authority_ph: '机关',
  pa_reg_insp_inspector_ph: '稽查人员',
  pa_reg_insp_due: '提交期限',
  pa_reg_insp_record: '登记调阅',
  pa_reg_insp_asked_label: '要求提交',
  pa_reg_insp_preview: '预览清单',
  pa_reg_insp_build: '生成检查包',
  pa_reg_insp_th_demanded: '调阅日',
  pa_reg_insp_th_by: '机关',
  pa_reg_insp_th_asked: '调阅内容',
  pa_reg_insp_th_due: '截止日',
  pa_reg_insp_th_answered: '已回复',
  pa_reg_insp_answered_prompt: '回复日期(YYYY-MM-DD)?',
  pa_reg_insp_answered_btn: '已回复…',
  pa_reg_chg_resolved: '决议日',
  pa_reg_chg_from_ph: '原',
  pa_reg_chg_to_ph: '新',
  pa_reg_chg_liquidation_due: '下一次清算报告应于 {{due}} 报送(自 {{since}} 起三个月)。',
  pa_reg_chg_th_resolved: '决议日',
  pa_reg_chg_th_what: '事项',
  pa_reg_chg_th_fromto: '原 → 新',
  pa_reg_chg_th_register_by: '登记截止日',
  pa_reg_chg_th_registered: '已登记',
  pa_reg_chg_registered_prompt: '向 DBD 登记的日期(YYYY-MM-DD)?',
  pa_reg_chg_receipt_prompt: '受理回执编号?',
  pa_reg_chg_registered_btn: '已登记…',
  pa_reg_dr_meeting: '会议日',
  pa_reg_dr_presented: '提交日',
  pa_reg_dr_body_th_label: '董事报告(泰文)',
  pa_reg_dr_body_en_label: '董事报告(英文)',
  pa_reg_vat_rev12: '12 个月收入 {{from}} – {{to}}',
  pa_reg_vat_source: '来源:{{source}}',
  pa_reg_vat_source_ledger: '账簿',
  pa_reg_vat_exceeded_head: '已超过 1.8 ล้านบาท。',
  pa_reg_vat_exceeded_body: '滚动十二个月收入已超过增值税登记起征点。',
  pa_reg_vat_notice_ack_by: '已确认 — 确认人',
  pa_reg_vat_notice_ack_tail: ',每日提醒已停止。',
  pa_reg_vat_notice_sending: '每日提醒 — 已发往 Telegram 与 office@slf-accounting.co,共 {{n}} 次。',
  pa_reg_vat_notice_sending_last: '每日提醒 — 已发往 Telegram 与 office@slf-accounting.co,共 {{n}} 次,最近一次 {{date}}。',
  pa_reg_vat_notice_stops: '有人点击邮件中的链接后即停止发送。',
  pa_reg_vat_notice_last_send: '上次发送:',
  pa_reg_vat_notice_resume: '重新开始发送',
  pa_reg_stock_cost_formula: '成本计算方法',
  pa_reg_stock_method_none: '— 未选择 —',
  pa_reg_stock_th_item: '存货项目',
  pa_reg_stock_th_qty: '数量',
  pa_reg_stock_th_cost: '成本',
  pa_reg_stock_th_market: '市价',
  pa_reg_stock_th_carried: '账面金额',
  pa_reg_stock_th_writedown: '计价减损',
  pa_reg_stock_total: '合计',
  pa_reg_stock_empty: '没有可计价的存货。',
  pa_reg_stock_struck_msg: '已确定计价。计价减损 {{amount}},记入待过账分录 {{doc}} — 请与其他待过账分录一并核对。',
  pa_reg_stock_struck_none: '已确定计价。无需计价减损。',
  pa_reg_stock_strike_btn: '确定计价',
  pa_reg_stock_struck_line: '已确定计价:{{n}} 项,{{date}} 由 {{name}} 确定。',
  pa_reg_firm_rule: 'Accounting Professions Act s.11 — 提供会计服务的法人须向 TFAC 登记,并就对第三人的赔偿责任缴存保证金;提供审计服务者须由注册会计师 (CPA) 签署约束,并与其审计师承担连带责任。',
  pa_reg_firm_regno_ph: 'TFAC 登记号',
  pa_reg_firm_registered: '登记日',
  pa_reg_firm_expires: '到期日',
  pa_reg_firm_guarantee_ph: '已缴存保证金(บาท)',
  pa_reg_firm_guarantee_form_ph: '形式(银行保函…)',
  pa_reg_firm_offers_audit: '提供审计服务',
  pa_reg_firm_cpa_name_ph: '签字注册会计师 (CPA)',
  pa_reg_firm_cpa_no_ph: 'CPA 执业证号',
  pa_reg_firm_expired: '已过期 — 该登记已于 {{date}} 到期。',
  pa_reg_firm_expiring: '{{date}} 到期 — 不足 60 天。',
  pa_reg_sec_incidents_title: '账簿或凭证灭失 / 损毁 (ss.15-16)',
  pa_reg_sec_incidents_sub: '自发现之日起 15 天内通知。原因不明的灭失推定为故意。',
  pa_reg_sec_cess_title: '停业 (s.17)',
  pa_reg_sec_cess_sub: '账簿须在 90 天内移交 DBD,可延长至 180 天。',
  pa_reg_sec_insp_title: '稽查调阅 (ss.22, 24, 25)',
  pa_reg_sec_insp_sub: '登记调阅,并将账簿、凭证与科目代码对照表打成一个检查包。',
  pa_reg_sec_chg_title: '变更登记(14 天)',
  pa_reg_sec_chg_sub: '资本、章程、董事;以及清算的三个月报送周期。',
  pa_reg_sec_dr_title_gloss: '董事报告 (CCC s.1198)',
  pa_reg_sec_dr_sub: '与资产负债表一并提交股东大会。法律未规定格式。',
  pa_reg_sec_stock_title: '期末存货计价,成本与市价孰低 (s.65 bis)',
  pa_reg_sec_stock_sub: '先进先出或加权平均,一经采用即锁定。(进出相抵不是成本计算方法。)',
  pa_reg_sec_vat_title: '增值税登记起征点 1.8 ล้าน',
  pa_reg_sec_vat_sub: '滚动十二个月收入对比 1,800,000 บาท。',
  pa_reg_sec_firm_title_gloss: '事务所自身的 TFAC 登记 (APA s.11)',
  pa_reg_sec_firm_sub: '关于 SLF 自身,不是客户:登记、保证金,以及签署约束的注册会计师 (CPA)。',

  // ── TaxLedgers — the six tax ledgers ──────────────────────────────────────────
  pa_tax_channel_label: '申报方式',
  pa_tax_channel_online: '网上申报',
  pa_tax_channel_paper: '纸质申报',
  pa_tax_channel_deadlines_paper: '月度申报表截止日为 7 日(增值税为 15 日);PND.50 为 150 天,PND.51 为两个月 — 不享有网上申报的延期。',
  pa_tax_channel_deadlines_online: '月度申报表截止日为 15 日(增值税为 23 日);PND.50 为 158 天,PND.51 为两个月零八天。',
  pa_tax_fx_rule_1: 's.65 bis (5) — 期末外币余额按泰国银行的汇率折算,并一贯采用同一方法。本系统没有汇率来源:汇率靠导入;在期末当日或之前没有汇率的余额,',
  pa_tax_fx_rule_not: '不会',
  pa_tax_fx_rule_2: '按任何汇率折算。',
  pa_tax_fx_method_label: '折算方法',
  pa_tax_fx_other_method_prompt: '获批准的方法是什么?由谁批准?',
  pa_tax_fx_method_none: '— 未选择 —',
  pa_tax_fx_no_method_warn: '尚未锁定折算方法。s.65 bis (5) 要求一贯采用同一方法,因此在选定之前不会进行任何重新折算 — 而一旦有会计期间按该方法重新折算过,就不能再更改。',
  pa_tax_fx_rate_label: '汇率',
  pa_tax_fx_kind_avg: 'BOT 平均汇率',
  pa_tax_fx_kind_buy: 'BOT 买入汇率',
  pa_tax_fx_kind_sell: 'BOT 卖出汇率',
  pa_tax_fx_source_ph: '来源',
  pa_tax_fx_rate_stored: '汇率已保存。',
  pa_tax_fx_nothing_imported: '没有导入任何数据。',
  pa_tax_fx_add_rate: '添加汇率',
  pa_tax_fx_account_none: '— 会计科目 —',
  pa_tax_fx_amount_fx_ph: '外币金额',
  pa_tax_fx_booked_ph: '账面泰铢金额',
  pa_tax_fx_add_balance: '添加外币余额',
  pa_tax_fx_th_account: '科目',
  pa_tax_fx_th_cur: '币种',
  pa_tax_fx_th_amount: '金额',
  pa_tax_fx_th_booked: '账面金额',
  pa_tax_fx_th_rate: '汇率',
  pa_tax_fx_th_at_rate: '按汇率折算',
  pa_tax_fx_th_diff: '收益 /(损失)',
  pa_tax_fx_empty: '本会计期间未记录外币余额。',
  pa_tax_fx_revalued_msg: '待过账分录 {{doc}} — 净额 {{amount}} · 重新折算的余额数:{{n}}。过账前请与其他待过账分录一并核对。',
  pa_tax_fx_revalue_btn: '重新折算 {{amount}}',
  pa_tax_rem_warn_1: '本系统对这两种申报表',
  pa_tax_rem_warn_norate: '不持有任何税率',
  pa_tax_rem_warn_2: ',也不持有其法定依据。适用于境内支付的 ท.ป.4/2528 税率属于另一项规定,不得在此使用 — 每一行的税率由顾问给出,并记录是谁录入的。',
  pa_tax_rem_lines: '{{n}} 行',
  pa_tax_rem_base: '计税基数 {{amount}}',
  pa_tax_rem_tax: '税额',
  pa_tax_rem_due: '截止日 {{date}}',
  pa_tax_rem_filed: '已申报',
  pa_tax_rem_payee_ph: '收款人',
  pa_tax_rem_country_ph: '国家',
  pa_tax_rem_nature_ph: '所得性质',
  pa_tax_rem_base_ph: '计税基数(泰铢)',
  pa_tax_rem_rate_ph: '税率 %',
  pa_tax_rem_basis_ph: '法定依据',
  pa_tax_rem_add: '添加',
  pa_tax_rem_th_paid: '支付日',
  pa_tax_rem_th_payee: '收款人',
  pa_tax_rem_th_for: '所得性质',
  pa_tax_rem_th_base: '计税基数',
  pa_tax_rem_th_rate: '税率',
  pa_tax_rem_th_tax: '税额',
  pa_tax_rem_th_basis: '法定依据',
  pa_tax_rem_not_recorded: '未记录',
  pa_tax_rem_empty: '本月申报表上没有任何记录。',
  pa_tax_rem_filed_prompt: '实际缴纳日期(YYYY-MM-DD)?',
  pa_tax_rem_mark_filed: '标记已申报',
  pa_tax_stamp_rule: 'ss.104 与 118 — 附表所列应税凭证必须足额贴花;未贴花的凭证在缴清印花税及附加金之前不得用作证据。事务所作为自身业务委任合同的承揽人,须按每 1,000 泰铢缴 1 泰铢 — 即 0.1% — 缴纳印花税。',
  pa_tax_stamp_party_ph: '交易对方',
  pa_tax_stamp_base_ph: '租金 / 费用 / 本金',
  pa_tax_stamp_pick_none: '— 哪一个金额? —',
  pa_tax_baht_amount: '{{amount}} 泰铢',
  pa_tax_stamp_one_act: '(单一行为)',
  pa_tax_stamp_several_acts: '(多次行为)',
  pa_tax_stamp_vat_exempt: '此金额已计增值税(免征)',
  pa_tax_stamp_record: '登记',
  pa_tax_stamp_schedule_head: 'ลักษณะแห่งตราสาร',
  pa_tax_stamp_number_inferred: '(项目编号系推断 — 来源将这七项作为一组给出)',
  pa_tax_stamp_band: ' · 附表给出 {{min}}–{{max}} 泰铢,是一个区间而不是公式',
  pa_tax_stamp_th_date: '日期',
  pa_tax_stamp_th_instrument: '应税凭证',
  pa_tax_stamp_th_party: '当事方',
  pa_tax_stamp_th_base: '计税基数',
  pa_tax_stamp_th_duty: '印花税额',
  pa_tax_stamp_th_stamped: '贴花',
  pa_tax_stamp_not_established: '尚未确定',
  pa_tax_stamp_exempt: '免征',
  pa_tax_stamp_unpaid: '未贴花',
  pa_tax_stamp_method_prompt: '以何种方式缴纳?请输入 adhesive(贴花)、impressed(压印)或 cash(现金)',
  pa_tax_stamp_paid_on_prompt: '缴纳日期(YYYY-MM-DD)?',
  pa_tax_stamp_duty_prompt: '已缴印花税额(泰铢)?',
  pa_tax_stamp_receipt_prompt: 'อ.ส. 收据号码?',
  pa_tax_stamp_mark_btn: '标记已贴花',
  pa_tax_stamp_empty: '登记簿上没有任何记录。',
  pa_tax_stamp_unstamped_count: '{{n}} 份未贴花。',
  pa_tax_stamp_duty_established: '已确定税额:{{amount}}',
  pa_tax_stamp_unquantified: ' · 其中 {{n}} 份尚无税额',
  pa_tax_loss_year_ph: '年度',
  pa_tax_loss_amount_ph: '亏损(泰铢)',
  pa_tax_loss_note_ph: '备注',
  pa_tax_loss_record: '登记亏损',
  pa_tax_loss_years_prompt: '结转年限(年)?',
  pa_tax_loss_basis_prompt: '这来自哪一条规定?(必填)',
  pa_tax_loss_set_period: '设定结转年限…',
  pa_tax_loss_th_year: '年度',
  pa_tax_loss_th_loss: '亏损',
  pa_tax_loss_th_used: '已使用',
  pa_tax_loss_th_left: '剩余',
  pa_tax_loss_th_expires: '到期',
  pa_tax_loss_th_used_in: '使用年度',
  pa_tax_loss_use_year_prompt: '在哪一年使用?',
  pa_tax_loss_use_amount_prompt: '使用多少(泰铢)?',
  pa_tax_loss_use_btn: '使用…',
  pa_tax_loss_total: '合计',
  pa_tax_loss_empty: '登记簿上没有亏损记录。',
  pa_tax_loss_unreconciled: '登记的合计数与各笔使用额之和不符。在查清之前,这里的任何数字都不宜用于申报。',
  pa_tax_rc_no_threshold: '尚未记录起征点,因此本项检查不会运行。s.105 只给出上限 — 增值税/特定业务税登记人为 1,000 泰铢,其他为 10,000 — 而税务局长公告的金额本系统并未持有。请记录该金额,或记录 0 表示每一笔收款都要开据;在该金额未知时,这是稳妥的读法。',
  pa_tax_rc_registrant_prompt: '是否为增值税/特定业务税登记人?请输入 vat_sbt 或 other',
  pa_tax_rc_threshold_prompt: '起征点(泰铢;0 = 每一笔收款;上限 {{ceiling}})',
  pa_tax_rc_notification_prompt: '税务局长公告号(如事务所持有)',
  pa_tax_rc_change_threshold: '更改起征点…',
  pa_tax_rc_record_threshold: '登记起征点…',
  pa_tax_rc_registrant_yes: '增值税/特定业务税登记人',
  pa_tax_rc_registrant_no: '非登记人',
  pa_tax_rc_scope_level: '{{scope}} 级',
  pa_tax_rc_th_received: '收款日',
  pa_tax_rc_th_against: '对应单据',
  pa_tax_rc_th_amount: '金额',
  pa_tax_rc_th_desc: '摘要',
  pa_tax_rc_all_covered: '此区间内每一笔已匹配的收款都有收据支持(已检查 {{n}} 笔)。',
  pa_tax_rc_daily_head: 's.105 ter 每日汇总',
  pa_tax_rc_daily_body: '共 {{n}} 天,合计 {{amount}}。',
  pa_tax_title: '税务台账',
  pa_tax_sec_fx_title: '汇率与期末重新折算',
  pa_tax_sec_fx_sub: 's.65 bis (5) — 一贯采用同一方法。此处没有汇率,就不会重新折算。',
  pa_tax_sec_rem_forms: 'ภ.ง.ด.54 · ภ.พ.36',
  pa_tax_sec_rem_title: '对外支付代扣代缴',
  pa_tax_sec_rem_sub: '这是一本登记簿,不是税率引擎:这里不知道这两种申报表的税率。',
  pa_tax_sec_stamp_title: '印花税',
  pa_tax_sec_stamp_sub: '律所或会计事务所会遇到的七项附表税率,以及尚未确定的部分。',
  pa_tax_sec_loss_title: '亏损结转登记簿',
  pa_tax_sec_loss_sub: '每个亏损年度一行;在未记录结转年限之前,不主张任何到期日。',
  pa_tax_sec_rc_doc: 'ใบรับ',
  pa_tax_sec_rc_title: '每笔收款均须出具收据 (s.105)',
  pa_tax_sec_rc_sub: '有收款但没有对应收据。在未记录起征点之前拒绝运行。',
  pa_cat_sec_title: 'คลังสินค้าของลูกค้า · 客户产品库 vs 存货账',
  pa_cat_sec_sub: '客户销售的产品就是法定存货品项。只出现在一边的,就是一笔没有成本记录的销售 —— 请在月度结账之前核对,不要之后。',
  pa_cat_rule: '客户在自己的门户里维护产品;บัญชีสินค้า 是这些产品落账的法定账簿。法定品项是主档:它的代码和泰文名称不能被价目表改写。',
  pa_cat_clean: '两份清单一致 —— 客户在卖的每一项都在存货账里。',
  pa_cat_n_gaps: '有 {{n}} 处不匹配',
  pa_cat_fix: '补齐无需任何人补充信息的 {{n}} 处',
  pa_cat_fixed: '已补 {{n}} 处 · 还有 {{left}} 处需要人来回答。',
  pa_cat_th_what: '类型',
  pa_cat_th_name: '品项',
  pa_cat_th_why: '为什么要紧',
  pa_cat_kind_product_missing_particulars: '资料不全',
  pa_cat_kind_product_unlinked: '未进账簿',
  pa_cat_kind_item_without_product: '客户开不了票',
  pa_cat_kind_product_undecided: '货品还是服务?',
  pa_cat_kind_no_merchant_tenant: '没有门户',
  pa_cat_kind_drift: '两边不一致',
  pa_imp_sec_title: 'นำเข้าความเคลื่อนไหวสินค้า · 导入出入库流水',
  pa_imp_sec_sub: '从客户自己的文件导入一整年的出入库。列的含义和「入/出」用词由你确认,系统不猜。',
  pa_imp_rule: '流水同时供 บัญชีสินค้า 和 รายงานสินค้าและวัตถุดิบ 使用。同一个文件重复上传是安全的 —— 已导入过的流水会被识别,不会写第二遍。',
  pa_imp_read: '读取表格',
  pa_imp_map_head: '每一列是什么',
  pa_imp_th_col: '列',
  pa_imp_th_sample: '前几个值',
  pa_imp_th_is: '含义',
  pa_imp_f_ignore: '— 不导入这一列 —',
  pa_imp_f_itemCode: '品项代码',
  pa_imp_f_itemName: '品项名称(用于匹配)',
  pa_imp_f_itemNameTh: '泰文名称(新建品项用)',
  pa_imp_f_unit: '计量单位',
  pa_imp_f_itemType: '种类 (ชนิด)',
  pa_imp_f_moveDate: '日期',
  pa_imp_f_direction: '出/入(单列)',
  pa_imp_f_quantity: '数量',
  pa_imp_f_qtyIn: '入库数量',
  pa_imp_f_qtyOut: '出库数量',
  pa_imp_f_value: '金额(仅入库)',
  pa_imp_f_docNo: '单据号',
  pa_imp_f_partyName: '供应商/客户',
  pa_imp_f_note: '备注',
  pa_imp_dir_head: '这张表里的词各是什么意思',
  pa_imp_dir_hint: '没选的词,它那些行会被跳过。方向读反了,进货就变成销售,而借贷照样是平的 —— 所以这里一个字都不假设。',
  pa_imp_dir_unset: '— 未选择 —',
  pa_imp_dir_in: '入库',
  pa_imp_dir_out: '出库',
  pa_imp_summary: '可导入 {{rows}} 条 · 跳过 {{skipped}} 行',
  pa_imp_create_items: '同时新建尚不在存货账里的 {{n}} 个品项',
  pa_imp_commit: '导入',
  pa_imp_dropped: '有 {{n}} 行出库带金额。这个金额不会导入:出库成本由该客户选定的计价公式算出,而表格那一列填的是售价。导入它既不会改变计价结果,还会让「在手金额」变成被收入冲减过的错数字。',
  pa_imp_uncreatable: '有 {{n}} 行的品项不在存货账里,而且该行没有代码、泰文名称和单位可供新建 —— 这些行被跳过,不会替它们编造任何东西。',
  pa_imp_th_row: '行',
  pa_imp_th_item: '品项',
  pa_imp_th_skipwhy: '跳过原因',
  pa_imp_done: '已导入 {{created}} 条 · 本来就有 {{dup}} 条 · 新建品项 {{items}} 个',
  pa_imp_done_failed: '有 {{n}} 行写不进去:',
  pa_imp_row_n: '第 {{n}} 行:',
  pa_imp_rerun_safe: '在文件里把这些行改好,再上传一次即可 —— 已经进去的会被识别,不会写第二遍。',
  pa_mv_sec_title: 'บันทึกรายการสินค้า · 手工录入一笔出入库',
  pa_mv_sec_sub: '用于文件里漏掉的那一笔,以及更正录错的。',
  pa_mv_rule: '手工录入的流水和导入的、以及税务发票自动生成的,一起放在 บัญชีสินค้า 里。⛔ 更正的方式是冲销,绝不是删除 —— 这一行在法定账簿里,原始那条留着,由一笔相反的流水说明实际发生了什么。',
  pa_mv_no_items: '这家客户还没有任何品项。请在客户的产品库里添加,或用表格导入时一并新建。',
  pa_mv_f_item: '品项',
  pa_mv_pick_item: '— 请选择品项 —',
  pa_mv_on_hand: '在手 {{n}} {{unit}}',
  pa_mv_f_date: '实际发生日期',
  pa_mv_f_direction: '出/入',
  pa_mv_f_qty: '数量',
  pa_mv_f_value: '成本 (บาท)',
  pa_mv_no_value_on_out: '出库不填成本。出库成本由该客户选定的计价公式(FIFO 或加权平均)算出 —— 在这里打一个数字,既不会改变计价结果,还会让「在手金额」变成被收入冲减过的错数字。',
  pa_mv_add: '录入',
  pa_mv_saved: '已录入。',
  pa_mv_backdated: '这是补录。s.87(3) 允许从实际发生到登记有三个工作日,报表上会显示每一笔各用了多久。',
  pa_mv_th_move: '方向',
  pa_mv_th_from: '来源',
  pa_mv_src_manual: '手工',
  pa_mv_src_import: '表格导入',
  pa_mv_src_invoice: '税务发票',
  pa_mv_src_reversal: '冲销',
  pa_mv_reverse: '冲销',
  pa_mv_reverse_confirm: '要冲销这笔 {{qty}} × {{name}} 吗?原始那条会留在账簿里,另加一笔相反的流水、日期为今天。此操作不可撤销 —— 若需要,可以再冲销这笔冲销。',
  pa_mv_reversed: '已冲销。原始那条留在账簿里,更正就记在它旁边。',
  pa_mv_already_reversed: '这笔之前已经冲销过了 —— 没有再加任何流水。',
  pa_mv_is_reversed: '已冲销',
};

export const acctTh: Record<string, string> = {
  pa_sb_period_label: 'รอบระยะเวลาบัญชี',
  pa_sb_open_books_gloss: 'เปิดบัญชี',
  pa_sb_open_books_why: 'พระราชบัญญัติการบัญชีกำหนดให้ปิดบัญชีทุกสิบสองเดือน จึงต้องมีรอบระยะเวลาบัญชีก่อนจะบันทึกรายการใดได้ หากเป็นรอบสิบสองเดือนตามปกติให้เว้นวันสิ้นสุดไว้ รอบแรกอาจสั้นกว่านั้นได้ และรอบถัดไปที่ไม่ครบสิบสองเดือนต้องบันทึกหนังสืออนุญาตจากสารวัตรใหญ่บัญชีไว้ก่อน ผังบัญชีของสำนักงานจะถูกคัดลอกเข้ามาพร้อมกัน',
  pa_sb_from: 'เริ่ม',
  pa_sb_to: 'สิ้นสุด',
  pa_sb_optional: 'ไม่บังคับ',
  pa_sb_open_period: 'เปิดรอบบัญชี',
  pa_sb_open_next: 'เปิดรอบถัดไป',
  pa_sb_status_closed: 'ปิดบัญชีแล้ว',
  pa_sb_n_entries_other: '{{count}} รายการ',
  pa_sb_nothing_posted: 'ยังไม่มีการบันทึกรายการในรอบนี้ — สร้างรายการรอตรวจด้านบน ตรวจสอบ แล้วจึงผ่านรายการ',

  pa_sb_close_gloss: 'ปิดบัญชี…',
  pa_sb_close_confirm: 'การปิดบัญชีไม่สามารถย้อนกลับได้ ผลกำไรขาดทุนจะถูกโอนเข้ากำไรสะสม ยอดยกไปจะถูกตรึง และเริ่มนับระยะเวลาเก็บรักษาเอกสารประกอบการลงบัญชีห้าปีของรอบนี้ ดำเนินการต่อหรือไม่',
  pa_sb_close_sum: 'รายได้ {{income}} − ค่าใช้จ่าย {{expense}} =',
  pa_sb_profit: 'กำไร',
  pa_sb_loss: 'ขาดทุน',
  pa_sb_close_drafts_other: 'ยังมีรายการรอตรวจ {{count}} รายการ — จัดการให้เรียบร้อยก่อน',
  pa_sb_close_outofrange_other: 'มีรายการที่บันทึกแล้ว {{count}} รายการอยู่นอกรอบระยะเวลาบัญชี',
  pa_sb_close_unbalanced: 'รอบระยะเวลาบัญชีนี้ไม่ดุล',
  pa_sb_close_it: 'ยืนยันปิดบัญชี',
  pa_sb_closed_detail: '{{entries}} รายการ · {{result}} {{amount}} · ยอดยกไป {{carried}} รายการ · ประทับเอกสาร {{vouchers}} ฉบับให้เก็บรักษาห้าปี',

  pa_sb_h_journals: 'สมุดรายวันทั้งห้าเล่ม',
  pa_sb_h_ledger: 'บัญชีแยกประเภทและงบทดลอง',
  pa_sb_h_statements: 'งบการเงิน',
  pa_sb_h_extras: 'งบกระแสเงินสด หมายเหตุ สินค้า และภาษีมูลค่าเพิ่ม',

  pa_sb_c_gl: 'บัญชีแยกประเภททั่วไป',
  pa_sb_c_tb: 'งบทดลอง',
  pa_sb_c_ar: 'บัญชีย่อยลูกหนี้',
  pa_sb_c_ap: 'บัญชีย่อยเจ้าหนี้',
  pa_sb_c_stock: 'บัญชีสินค้า — เฉพาะกิจการที่มีสินค้า',
  pa_sb_c_goods: 'รายงานสินค้าและวัตถุดิบ',
  pa_sb_c_vatout: 'รายงานภาษีขาย — เดือนนี้',
  pa_sb_c_vatin: 'รายงานภาษีซื้อ — เดือนนี้',

  pa_sb_pl_line: 'รายได้ {{income}} − ค่าใช้จ่าย {{expense}}',
  pa_sb_assets_line: 'สินทรัพย์ {{amount}}',
  pa_sb_unbalanced_why: 'สินทรัพย์ไม่เท่ากับหนี้สินรวมส่วนของเจ้าของ',

  pa_fs_bound_what: 'งบการเงินฉบับรวมเล่ม: ปก สารบัญพร้อมเลขหน้า และเลขหน้าต่อเนื่องชุดเดียว',
  pa_fs_bound_build: 'จัดทำฉบับรวมเล่ม',
  pa_fs_bound_building: 'กำลังจัดเล่ม…',
  pa_fs_cover_issues: 'ข้อสังเกตที่จะพิมพ์บนปก',
  pa_fs_pick_least: 'เลือกอย่างน้อยหนึ่งงบ',
  pa_fs_sfp: 'งบแสดงฐานะการเงิน',
  pa_fs_pl: 'งบกำไรขาดทุน',
  pa_fs_sce: 'งบแสดงการเปลี่ยนแปลงส่วนของเจ้าของ',
  pa_fs_cf: 'งบกระแสเงินสด',
  pa_fs_notes: 'หมายเหตุประกอบงบการเงิน',
  pa_fs_wa: 'บัญชีทำการ — ยื่นพร้อม ภ.ง.ด.50',

  pa_sb_foot_books: 'สมุดบัญชีพิมพ์เป็นภาษาไทย มีปกแสดงชื่อ {{client}} ชนิดของสมุดบัญชีและเล่มที่ และเรียงเลขหน้าต่อเนื่อง ตามที่ประกาศกรมพัฒนาธุรกิจการค้า พ.ศ. 2544 กำหนด',
  pa_sb_foot_statements: 'งบการเงินจัดทำจากสมุดบัญชีเหล่านั้นตามเกณฑ์คงค้าง ยังไม่ได้จัดรูปแบบเป็นรายการย่อตามประกาศกรมพัฒนาธุรกิจการค้า พ.ศ. 2566 จึงยังไม่ใช่แบบที่ยื่นต่อกรมพัฒนาธุรกิจการค้า — การจัดรูปแบบดังกล่าวรออยู่ที่ตัวบทประกาศฉบับจริง',

  // ── AccountingBooks — the client picker ───────────────────────────────────────
  pa_books_all_clients: 'ลูกค้าทั้งหมด',
  pa_books_title: 'สมุดบัญชีตามกฎหมาย',
  pa_books_intro: 'ผังบัญชี รอบระยะเวลาบัญชี สมุดรายวันทั้งห้าเล่ม บัญชีแยกประเภท การปิดบัญชีสิ้นปี และงบการเงิน — ลูกค้าบัญชีทุกรายจะแสดงที่นี่ รวมถึงรายที่ยังไม่มีใบแจ้งหนี้ เพราะบริษัทที่จดทะเบียนใหม่คือรายที่ต้องเปิดรอบระยะเวลาบัญชีมากที่สุด',
  pa_books_search_ph: 'ค้นหาลูกค้า…',
  pa_books_empty: 'ไม่พบลูกค้าบัญชีที่ตรงกัน',
  pa_books_drafts_pill: '{{n}} ร่าง',
  pa_books_no_period: 'ยังไม่เปิดบัญชี',
  pa_books_pill_closed: 'ปิดแล้ว',
  pa_books_pill_open: 'เปิดอยู่',
  pa_books_card_counts: '{{accounts}} บัญชี · {{entries}} รายการที่บันทึกบัญชีแล้ว',
  pa_books_card_vat: 'VAT',

  // ── Bookkeeping — the invoice-line-item books ─────────────────────────────────
  pa_bk_mon_jan: 'ม.ค.',
  pa_bk_mon_feb: 'ก.พ.',
  pa_bk_mon_mar: 'มี.ค.',
  pa_bk_mon_apr: 'เม.ย.',
  pa_bk_mon_may: 'พ.ค.',
  pa_bk_mon_jun: 'มิ.ย.',
  pa_bk_mon_jul: 'ก.ค.',
  pa_bk_mon_aug: 'ส.ค.',
  pa_bk_mon_sep: 'ก.ย.',
  pa_bk_mon_oct: 'ต.ค.',
  pa_bk_mon_nov: 'พ.ย.',
  pa_bk_mon_dec: 'ธ.ค.',
  pa_bk_individual: 'บุคคลธรรมดา',
  pa_bk_item_remove: 'ลบ',
  pa_bk_zero_rate: '0%',
  pa_bk_loading: 'กำลังโหลด…',
  pa_bk_books_link: 'สมุดบัญชี',
  pa_bk_books_link_title: 'ผังบัญชี สมุดรายวัน บัญชีแยกประเภท ปิดบัญชี และงบการเงิน',
  pa_bk_err_items_load: 'โหลดรายการไม่ได้',
  pa_bk_err_not_saved: 'ไม่ได้บันทึก',
  pa_bk_err_add: 'เพิ่มไม่ได้',
  pa_bk_err_delete: 'ลบไม่ได้',
  pa_bk_err_clear: 'ลบทั้งหมดไม่ได้',
  pa_bk_err_regen: 'สร้างใหม่จากรูปไม่สำเร็จ',
  pa_bk_err_month_load: 'โหลดเดือนนี้ไม่ได้',
  pa_bk_err_photo: 'โหลดรูปไม่ได้',

  // ── EntityInfo — juristic form and dividends ──────────────────────────────────
  pa_entity_form_not_recorded: 'ยังไม่ได้บันทึกรูปแบบนิติบุคคล',
  pa_entity_unknown_why: 'ลูกค้ารายนี้มีหน้าที่จัดทำบัญชีตาม s.8 หรือไม่ งบการเงินใช้แบบใดของ DBD และจะยกเว้นการสอบบัญชีได้หรือไม่ ทั้งหมดขึ้นอยู่กับข้อนี้',
  pa_entity_must_keep: 'ต้องจัดทำบัญชี (ม.8)',
  pa_entity_not_required: 'ไม่ใช่ผู้มีหน้าที่จัดทำบัญชี',
  pa_entity_dbd_form: 'DBD แบบ {{n}}',
  pa_entity_reg_capital_val: 'ทุนจดทะเบียน {{amount}}',
  pa_entity_registered_on_val: 'จดทะเบียน {{date}}',
  pa_entity_dividend: 'เงินปันผล',
  pa_entity_f_form: 'รูปแบบ',
  pa_entity_f_form_select: '— เลือก —',
  pa_entity_form_dbd_suffix: '(แบบ {{n}})',
  pa_entity_f_capital: 'ทุนจดทะเบียน (บาท)',
  pa_entity_f_registered_on: 'วันจดทะเบียน',
  pa_entity_div_law: 'ประมวลกฎหมายแพ่งและพาณิชย์ ม.1201–1202 — เงินปันผลจ่ายได้จากกำไรเท่านั้น จ่ายไม่ได้ในขณะที่ยังมีผลขาดทุนสะสม และต้องจัดสรรเงินปันผลนั้นอย่างน้อย 5% เข้าทุนสำรองจนกว่าทุนสำรองจะถึงหนึ่งในสิบของทุนจดทะเบียน และต้องจ่ายภายในหนึ่งเดือนนับแต่วันที่มีมติ',
  pa_entity_div_amount: 'จำนวน (บาท)',
  pa_entity_div_resolution_date: 'วันที่มีมติ',
  pa_entity_div_payable_account: 'บัญชีเงินปันผลค้างจ่าย',
  pa_entity_div_retained: 'กำไรสะสม {{amount}}',
  pa_entity_div_reserve: 'ทุนสำรอง {{amount}}',
  pa_entity_div_ceiling: 'เพดาน {{amount}}',
  pa_entity_div_appropriation: 'จัดสรรทุนสำรอง {{amount}}',
  pa_entity_div_create: 'สร้างร่าง',
  pa_entity_div_created: 'สร้าง {{doc}} เป็นร่างแล้ว',
  pa_entity_div_created_appr: 'สร้าง {{doc}} เป็นร่างแล้ว โดยจัดสรร {{amount}} เข้าทุนสำรอง',

  // ── DraftEntries — generate, review, post ─────────────────────────────────────
  pa_jnl_cash: 'เงินสด',
  pa_jnl_bank: 'ธนาคาร',
  pa_jnl_purchase: 'รายวันซื้อ',
  pa_jnl_sale: 'รายวันขาย',
  pa_jnl_general: 'รายวันทั่วไป',
  pa_draft_title: 'รายการรอตรวจ',
  pa_draft_sub_pending: 'รายการที่นี่จะยังไม่อยู่ในสมุดบัญชีจนกว่าคุณจะผ่านรายการ',
  pa_draft_sub_empty: 'มาจากสมุดย่อยภาษีมูลค่าเพิ่ม — รายการขายและซื้อที่บันทึกไว้แล้ว',
  pa_draft_generate: 'สร้างจากสมุดย่อย',
  pa_draft_hide: 'ซ่อน',
  pa_draft_review: 'ตรวจสอบ',
  pa_draft_post_all: 'ผ่านรายการทั้งหมด {{n}} รายการ',
  pa_draft_post: 'ผ่านรายการ',
  pa_draft_discard_title: 'ทิ้งร่างนี้',
  pa_draft_discard_confirm: 'ทิ้งร่างของ {{docNo}} หรือไม่ แถวในสมุดย่อยยังอยู่ — สร้างใหม่ได้อีกครั้ง',
  pa_draft_gen_created: 'สร้างรายการรอตรวจ {{n}} รายการ',
  pa_draft_gen_skipped: 'มีรายการรอตรวจอยู่แล้ว {{n}} รายการ',
  pa_draft_gen_needs_account: 'ต้องเลือกบัญชีค่าใช้จ่าย {{n}} รายการ',
  pa_draft_gen_blocked: 'ปฏิเสธ {{n}} บรรทัด — {{list}}',
  pa_draft_gen_blocked_more: ' … และอีก {{n}} รายการ',
  pa_draft_no_doc_no: '(ไม่มีเลขที่)',
  pa_draft_posted_single: 'ผ่านรายการแล้ว',
  pa_draft_posted_n: 'ผ่านรายการแล้ว {{n}} รายการ',

  // ── ChartOfAccounts ───────────────────────────────────────────────────────────
  pa_chart_title: 'ผังบัญชี',
  pa_chart_counts: '{{n}} บัญชี · ใช้งานแล้ว {{used}} บัญชี',
  pa_chart_empty: 'ยังไม่มีบัญชี — คัดลอกแม่แบบของสำนักงานเพื่อเริ่มต้น',
  pa_chart_copy_template: 'คัดลอกแม่แบบของสำนักงาน',
  pa_chart_sync_template: 'ซิงค์จากแม่แบบ',
  pa_chart_sync_title: 'เพิ่มบัญชีในแม่แบบที่ลูกค้ารายนี้ยังไม่มี',
  pa_chart_add_account: 'เพิ่มบัญชี',
  pa_chart_open: 'เปิด',
  pa_chart_hide: 'ซ่อน',
  pa_chart_ph_code: 'รหัสบัญชี',
  pa_chart_ph_name_th: 'ชื่อบัญชี (ภาษาไทย)',
  pa_chart_ph_name_en: 'ชื่อบัญชี (ภาษาอังกฤษ)',
  pa_chart_save: 'บันทึก',
  pa_chart_cancel: 'ยกเลิก',
  pa_chart_rename: 'เปลี่ยนชื่อ',
  pa_chart_switch_off: 'ปิดใช้',
  pa_chart_switch_on: 'เปิดใช้',
  pa_chart_name_th_manual: 'ชื่อไทยเป็นคู่มือแปลรหัสบัญชีตาม ม.21(1) — ต้องระบุ',
  pa_chart_template_complete: 'ผังบัญชีนี้มีบัญชีครบทุกบัญชีในแม่แบบของสำนักงานแล้ว',
  pa_chart_remove_confirm: 'ลบบัญชี {{code}} {{name}} หรือไม่?',
  pa_chart_class_asset: 'สินทรัพย์',
  pa_chart_class_liability: 'หนี้สิน',
  pa_chart_class_equity: 'ส่วนของเจ้าของ',
  pa_chart_class_income: 'รายได้',
  pa_chart_class_expense: 'ค่าใช้จ่าย',
  pa_chart_cf_prefixed: 'งบกระแสเงินสด: {{section}}',
  pa_chart_cf_none: '— ยังไม่ระบุ —',
  pa_chart_cf_operating: 'กิจกรรมดำเนินงาน',
  pa_chart_cf_investing: 'กิจกรรมลงทุน',
  pa_chart_cf_financing: 'กิจกรรมจัดหาเงิน',
  pa_chart_cf_cash: 'เงินสดและรายการเทียบเท่า',
  pa_chart_cf_noncash: 'ไม่ใช่เงินสด',
  pa_chart_cf_missing_warn: '⚠️ บัญชีสินทรัพย์/หนี้สิน/ส่วนของเจ้าของ ที่ไม่ได้เลือกหมวดกระแสเงินสด จะทำให้งบกระแสเงินสดไม่ดุล (ต่างกันเท่ากับจำนวนเงินที่ผ่านบัญชีนี้พอดี)',
  pa_chart_cf_missing_title: 'งบกระแสเงินสดจะไม่ดุลจนกว่าบัญชีนี้จะเลือกหมวด',
  pa_chart_cf_missing_pill: 'ไม่มีงบกระแสเงินสด',

  // ── CompliancePanel — Accounting Act records ──────────────────────────────────
  pa_compliance_title: 'ตรวจสอบบัญชีและการยื่นต่อกรมพัฒนาธุรกิจการค้า',
  pa_compliance_file_by: 'ยื่นภายใน {{date}}',
  pa_compliance_due_unknown: 'ยังคำนวณกำหนดยื่นไม่ได้',
  pa_compliance_record_meeting_audit: 'บันทึกการประชุม/ตรวจสอบ',
  pa_compliance_record_filing: 'บันทึกการยื่น',
  pa_compliance_audit_unknown: '⚠ ยังสรุปไม่ได้ว่าต้องตรวจสอบบัญชีหรือไม่',
  pa_compliance_audit_required: 'ต้องตรวจสอบบัญชีโดยผู้สอบบัญชีรับอนุญาต',
  pa_compliance_audit_exempt: 'ได้รับยกเว้นการตรวจสอบบัญชี',
  pa_compliance_unbacked: '{{n}} รายการไม่มีเอกสารประกอบ ไม่มีเลขที่ และไม่ได้ระบุที่มา — ผ่านรายการไม่ได้ (ข้อ 5)',
  pa_compliance_filed_msg: 'บันทึกการยื่น {{kind}} วันที่ {{date}} แล้ว',
  pa_compliance_agm_due: 'ประชุมใหญ่ภายใน {{date}}',
  pa_compliance_agm_held: 'ประชุมเมื่อ {{date}}',
  pa_compliance_agm_not_held: 'ยังไม่ได้ประชุม',
  pa_compliance_statements_due: 'ส่งงบให้ผู้ถือหุ้นภายใน {{date}}',
  pa_compliance_bojo5_due: 'ยื่น บอจ.5 ภายใน {{date}}',
  pa_compliance_filed_label: 'ยื่นแล้ว:',
  pa_compliance_bookkeeper_label: 'ผู้ทำบัญชี:',
  pa_compliance_tfac: '(TFAC {{no}})',
  pa_compliance_tfac_lapsed: 'หมดสมาชิกภาพ',
  pa_compliance_no_bookkeeper: 'ยังไม่ได้แต่งตั้ง (Accounting Act s.19)',
  pa_compliance_eacct_pending: 'ยังไม่ได้แจ้ง e-Accountant สำหรับ {{name}} (ภายใน 30 วันนับแต่ {{date}})',
  pa_compliance_notified_today: 'แจ้งแล้ววันนี้',
  pa_compliance_f_agm_date: 'วันประชุมใหญ่',
  pa_compliance_f_statements_sent: 'ส่งงบให้ผู้ถือหุ้น',
  pa_compliance_f_audit_completed: 'ตรวจสอบเสร็จ',
  pa_compliance_f_opinion: 'ความเห็น',
  pa_compliance_opinion_unqualified: 'ไม่มีเงื่อนไข',
  pa_compliance_opinion_qualified: 'มีเงื่อนไข',
  pa_compliance_opinion_adverse: 'ไม่ถูกต้อง',
  pa_compliance_opinion_disclaimer: 'ไม่แสดงความเห็น',
  pa_compliance_f_auditor: 'ผู้สอบบัญชี',
  pa_compliance_f_auditor_license: 'เลขทะเบียน',
  pa_compliance_f_auditor_kind: 'ประเภท',
  pa_compliance_auditor_cpa: 'ผู้สอบบัญชีรับอนุญาต (CPA)',
  pa_compliance_auditor_ta: 'ผู้สอบบัญชีภาษีอากร (TA)',
  pa_compliance_f_audit_fee: 'ค่าสอบบัญชี',
  pa_compliance_save: 'บันทึก',
  pa_compliance_f_kind: 'ยื่นอะไร',
  pa_compliance_kind_fs: 'งบการเงิน',
  pa_compliance_kind_fs_gloss: 'Financial statements',
  pa_compliance_kind_bojo5: 'บอจ.5',
  pa_compliance_kind_bojo5_gloss: 'Shareholder list',
  pa_compliance_f_filed_on: 'วันที่ยื่น',
  pa_compliance_f_receipt_no: 'เลขที่ใบรับ',
  pa_compliance_record: 'บันทึก',

  // ── FixedAssets — the asset register ──────────────────────────────────────────
  pa_assets_register_name: 'ทะเบียนทรัพย์สิน',
  pa_assets_title: 'Fixed assets',
  pa_assets_summary: 'ทรัพย์สิน {{n}} รายการ · มูลค่าสุทธิ {{nbv}}',
  pa_assets_empty: 'ยังไม่มีรายการในทะเบียน',
  pa_assets_due: 'ค่าเสื่อมที่ต้องคิดในงวดนี้ {{amount}}',
  pa_assets_open_register: 'ทะเบียน',
  pa_assets_charge_dep: 'คิดค่าเสื่อม {{amount}}',
  pa_assets_add: 'ทรัพย์สิน',
  pa_assets_open: 'เปิด',
  pa_assets_hide: 'ซ่อน',
  pa_assets_registered_msg: 'บันทึก {{no}} แล้ว อัตราปีละ {{rate}}%',
  pa_assets_dep_draft: 'ทรัพย์สิน {{n}} รายการ {{amount}} — สร้างเป็นร่างเพื่อตรวจสอบ',
  pa_assets_dep_none: 'คิดค่าเสื่อมของทรัพย์สินทุกรายการในงวดนี้แล้ว',
  pa_assets_f_class: 'ประเภท',
  pa_assets_no_dep: 'ไม่หักค่าเสื่อม',
  pa_assets_f_acquired: 'ได้มาเมื่อ',
  pa_assets_f_cost: 'ราคาทุน',
  pa_assets_f_salvage: 'มูลค่าคงเหลือ',
  pa_assets_f_rate: 'อัตรา %',
  pa_assets_f_name_th: 'รายการ (ภาษาไทย) — ต้องระบุ',
  pa_assets_f_name_en: 'รายการ (ภาษาอังกฤษ)',
  pa_assets_f_acct_asset: 'บัญชีทรัพย์สิน',
  pa_assets_f_acct_accum: 'ค่าเสื่อมสะสม',
  pa_assets_f_acct_expense: 'ค่าใช้จ่ายค่าเสื่อม',
  pa_assets_max_rate_source: 'อัตราสูงสุดตาม {{source}}',
  pa_assets_cancel: 'ยกเลิก',
  pa_assets_submit: 'บันทึก',
  pa_assets_th_no: 'เลขที่',
  pa_assets_th_item: 'รายการ',
  pa_assets_th_acquired: 'ได้มา',
  pa_assets_th_cost: 'ราคาทุน',
  pa_assets_th_accum: 'ค่าเสื่อมสะสม',
  pa_assets_th_nbv: 'มูลค่าสุทธิ',
  pa_assets_th_year: 'ปีนี้',
  pa_assets_disposed_on: 'จำหน่าย {{date}}',
  pa_assets_days: '{{n}} วัน',

  // ── ManualEntry — the general journal ─────────────────────────────────────────
  pa_manual_book_general_gloss: 'General',
  pa_manual_book_cash_gloss: 'Cash',
  pa_manual_book_bank_gloss: 'Bank',
  pa_manual_book_purchase_gloss: 'Purchase',
  pa_manual_book_sale_gloss: 'Sale',
  pa_manual_title: 'รายการปรับปรุง',
  pa_manual_subtitle: 'รายการค้างรับค้างจ่าย ค่าใช้จ่ายจ่ายล่วงหน้า ค่าเสื่อม และประมาณการหนี้สิน — และการกลับรายการ ซึ่งเป็นวิธีเดียวที่จะแก้ไขรายการที่บันทึกบัญชีแล้ว',
  pa_manual_new_btn: 'บันทึกรายการ',
  pa_manual_reverse_btn: 'กลับรายการ',
  pa_manual_sweep_title: 'ดึงรายการค้างรับค้างจ่ายจากรอบระยะเวลาบัญชีก่อนที่ต้องกลับรายการในรอบนี้เข้ามา',
  pa_manual_accruals_btn: 'รายการค้างรับค้างจ่าย',
  pa_manual_late_entries: 'รายการที่ผ่านรายการเกิน 15 วันนับจากวันที่ของรายการ: {{n}} รายการ — ช้าที่สุด {{days}} วัน ({{docNo}})',
  pa_manual_late_rule: 'DBD กำหนดให้บันทึกสมุดรายวันภายใน 15 วันนับจากวันที่เกิดรายการ',
  pa_manual_closing_overdue: 'รอบระยะเวลาบัญชีนี้สิ้นสุดเมื่อ {{date}} ผ่านมาแล้ว {{days}} วัน และยังเปิดอยู่ ต้องปิดบัญชีภายใน 60 วัน',
  pa_manual_saved: 'สร้างรายการรอตรวจ {{docNo}} แล้ว — ตรวจสอบด้านล่างแล้วผ่านรายการ',
  pa_manual_reverse_confirm: 'กลับรายการ {{docNo}} หรือไม่? รายการเดิมยังอยู่ในสมุดบัญชีพร้อมเลขหน้าเดิม การแก้ไขจะถูกสร้างเป็นรายการรอตรวจไว้ข้างกัน',
  pa_manual_reversed_msg: 'สร้าง {{docNo}} เป็นรายการรอตรวจ วันที่ {{date}}',
  pa_manual_sweep_done: 'สร้างรายการกลับรายการค้างรับค้างจ่าย {{n}} รายการเป็นรายการรอตรวจ',
  pa_manual_sweep_none: 'ไม่มีรายการค้างรับค้างจ่ายจากรอบระยะเวลาบัญชีก่อนที่ต้องกลับรายการเข้ารอบนี้',
  pa_manual_f_date: 'วันที่',
  pa_manual_f_book: 'สมุด',
  pa_manual_f_bank_acct: 'เลขที่บัญชีธนาคาร',
  pa_manual_f_bank_acct_hint: 'หนึ่งเล่มต่อหนึ่งเลขที่บัญชี',
  pa_manual_f_bank_acct_ph: 'เช่น 230-3-57579-3',
  pa_manual_bank_acct_new: 'บัญชีอื่น…',
  pa_manual_bank_acct_used: 'มีแล้ว {{n}} รายการ',
  pa_manual_bank_acct_none: 'ยังไม่มีบัญชีธนาคารสำหรับลูกค้ารายนี้ — พิมพ์เลขที่บัญชี แล้วครั้งต่อไปจะแสดงให้เลือก',
  pa_manual_f_docno: 'เลขที่เอกสาร',
  pa_manual_f_docno_hint: '(เว้นว่าง → ระบบออกเลข JV)',
  pa_manual_f_docno_ph: 'JV-… ระบบออกให้',
  pa_manual_f_reverse_on: 'กลับรายการวันที่',
  pa_manual_f_reverse_on_hint: '(รายการค้างรับค้างจ่าย)',
  pa_manual_narration_ph: 'คำอธิบายรายการ — ต้องระบุ',
  pa_manual_calcnote_ph: 'วิธีคำนวณ — ต้องระบุเมื่อไม่มีเอกสารประกอบ (ข้อ 4)',
  pa_manual_th_account: 'บัญชี',
  pa_manual_th_dr: 'เดบิต',
  pa_manual_th_cr: 'เครดิต',
  pa_manual_pick_account: '— เลือกบัญชี —',
  pa_manual_add_line: 'บรรทัด',
  pa_manual_no_amounts: 'ยังไม่มีจำนวนเงิน',
  pa_manual_balances: 'ดุล',
  pa_manual_out_by: 'ต่างกัน {{amount}}',
  pa_manual_save_draft: 'บันทึกเป็นร่าง',
  pa_manual_reverse_note: 'รายการที่บันทึกบัญชีแล้วจะไม่ถูกยกเลิกการผ่านรายการ รายการเดิมยังอยู่ที่เดิมพร้อมเลขหน้า และการแก้ไขคือรายการใหม่ที่บันทึกไว้ข้างกัน ซึ่งเป็นสิ่งที่ทำให้เกิดร่องรอยการตรวจสอบ',
  pa_manual_search_ph: 'ค้นหาเลขที่เอกสารหรือคำอธิบาย…',
  pa_manual_none_posted: 'ไม่พบรายการที่บันทึกบัญชีแล้วที่ตรงกัน',
  pa_manual_reversed_by: 'กลับแล้ว {{docNo}}',

  // ── Headless — the records with no other home ─────────────────────────────────
  pa_headless_wa_rule_a: 'Revenue Code s.68 bis — เพื่อการภาษี ทุกบริษัทต้องจัดทำงบดุล ',
  pa_headless_wa_rule_b: 'บัญชีทำการ',
  pa_headless_wa_rule_c: ' และบัญชีกำไรขาดทุน ⚠️ ระบบนี้ไม่มีรูปแบบทางการของ DBD จึงพิมพ์ตามรูปแบบของระบบนี้เอง',
  pa_headless_wa_income: 'รายได้',
  pa_headless_wa_cost: 'ต้นทุน',
  pa_headless_wa_gross: 'กำไรขั้นต้น',
  pa_headless_wa_expenses: 'ค่าใช้จ่าย',
  pa_headless_wa_net: 'กำไร(ขาดทุน)สุทธิ',
  pa_headless_wa_mismatch_name: 'งบทดลอง',
  pa_headless_wa_mismatch: 'ไม่ตรงกัน',
  pa_headless_wa_doc_name: 'บัญชีทำการ',
  pa_headless_wa_doc: 'Working account',
  pa_headless_fw_rule: 'แบบ 1, 2, 4 และ 5 จัดทำบนฐาน TFRS สำหรับ NPAEs; แบบ 3 บนฐาน TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ กิจการ NPAE อาจเลือกใช้ TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะได้หากใช้ทั้งฉบับ และต้องเปิดเผยการเปลี่ยนรูปแบบไว้ในหมายเหตุประกอบงบการเงิน',
  pa_headless_fw_period: 'บันทึกไว้กับรอบระยะเวลาบัญชีนี้',
  pa_headless_fw_period_tail: ' — รอบที่ปิดบัญชีแล้วไม่มีการปรับปรุงย้อนหลัง',
  pa_headless_fw_none: '— ยังไม่ได้บันทึก —',
  pa_headless_fw_npae: 'TFRS สำหรับ NPAEs',
  pa_headless_fw_pae: 'TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ',
  pa_headless_fw_dbd_form: 'DBD แบบ {{form}}',
  pa_headless_fw_locked: 'ปิดบัญชีแล้ว — ล็อกแล้ว',
  pa_headless_sh_rule: 'CCC — บริษัทต้องจัดทำทะเบียนผู้ถือหุ้นตั้งแต่จดทะเบียนจัดตั้ง และยื่น บอจ.5 ตามสถานะ ณ วันประชุมภายใน 14 วัน ⚠️ ระบบนี้ไม่มีแบบ บอจ.5 เปล่าของนายทะเบียน จึงอ่านและตรวจรายชื่อได้ แต่พิมพ์ลงแบบทางการไม่ได้',
  pa_headless_sh_as_at: 'ณ วันที่',
  pa_headless_sh_holders: '{{n}} ผู้ถือหุ้น',
  pa_headless_sh_shares: '{{n}} หุ้น',
  pa_headless_sh_form_name: 'บอจ.5',
  pa_headless_sh_form_not_held: 'ไม่มีแบบฟอร์ม',
  pa_headless_sh_name_ph: 'ชื่อผู้ถือหุ้น',
  pa_headless_sh_individual: 'บุคคลธรรมดา',
  pa_headless_sh_juristic: 'นิติบุคคล',
  pa_headless_sh_idno: 'เลขประจำตัว',
  pa_headless_sh_nationality: 'สัญชาติ',
  pa_headless_sh_shares_ph: 'จำนวนหุ้น',
  pa_headless_sh_paid_per_share: 'ชำระแล้ว/หุ้น',
  pa_headless_sh_held_from: 'ถือตั้งแต่',
  pa_headless_sh_add: 'เพิ่ม',
  pa_headless_sh_th_name: 'ชื่อ',
  pa_headless_sh_th_kind: 'ประเภท',
  pa_headless_sh_th_shares: 'หุ้น',
  pa_headless_sh_end_prompt: 'สิ้นสุดการถือหุ้นเมื่อ (YYYY-MM-DD)?',
  pa_headless_sh_end: 'สิ้นสุด…',
  pa_headless_sh_empty: 'ยังไม่มีการถือหุ้นที่บันทึกไว้ ณ วันที่นี้',
  pa_headless_pkg_rule: 'งบการเงินและรายงานของผู้สอบบัญชียื่นผ่าน DBD e-Filing: งบการเงินเป็น XBRL รายงานของผู้สอบบัญชีเป็นภาพสแกนขาวดำ และรายชื่อผู้ถือหุ้น',
  pa_headless_pkg_unit: 'หน่วย',
  pa_headless_pkg_file: 'ไฟล์ {{id}}',
  pa_headless_pkg_declared: 'แจ้งแล้ว',
  pa_headless_pkg_not_attached: 'ยังไม่แนบ',
  pa_headless_pkg_not_producible: 'ระบบนี้ผลิตไม่ได้',
  pa_headless_pkg_file_prompt: 'File ID ของ {{slot}}?',
  pa_headless_pkg_attach: 'แนบ…',
  pa_headless_pkg_declare_bw: 'แจ้งว่ารายงานของผู้สอบบัญชีเป็นขาวดำ',
  pa_headless_cit_rule: 'Revenue Code s.65 ter — บัญชีใดหักเป็นรายจ่ายทางภาษีเงินได้นิติบุคคลได้ และบัญชีใดหักไม่ได้ ⚠️ ระบบนี้มีเฉพาะอนุมาตรา (1)–(6); ส่วนที่เหลือของ s.65 ter ยังไม่ได้มา',
  pa_headless_cit_th_account: 'บัญชี',
  pa_headless_cit_th_movement: 'ความเคลื่อนไหว',
  pa_headless_cit_th_treatment: 'การปฏิบัติทางภาษีเงินได้นิติบุคคล',
  pa_headless_cit_th_note: 'หมายเหตุ',
  pa_headless_cit_undecided: '— ยังไม่ได้ระบุ —',
  pa_headless_cit_yes: 'หักได้',
  pa_headless_cit_no: 'หักไม่ได้',
  pa_headless_cit_limited: 'หักได้บางส่วน',
  pa_headless_cit_empty: 'ไม่มีรายการที่ต้องตรวจสอบในรอบนี้',
  pa_headless_fx_rule_a: 'Revenue Code s.86/4 — ใบกำกับภาษีต้องออกเป็นเงินบาท เว้นแต่กรมสรรพากรอนุมัติให้ใช้สกุลเงินอื่น ',
  pa_headless_fx_rule_b: 'ขณะนี้มีการบังคับใช้ด่านนี้เมื่อออกหรือแก้ไขใบกำกับภาษี',
  pa_headless_fx_rule_c: ': จนกว่าจะบันทึกการอนุมัติไว้ที่นี่ ใบกำกับภาษีที่ไม่ใช่ THB จะถูกปฏิเสธ',
  pa_headless_fx_approval_no: 'เลขที่หนังสืออนุมัติกรมสรรพากร',
  pa_headless_fx_approved_on: 'อนุมัติเมื่อ',
  pa_headless_fx_effective_from: 'มีผลตั้งแต่',
  pa_headless_fx_effective_to: 'ถึง',
  pa_headless_fx_recorded: 'บันทึกการอนุมัติสำหรับ {{currency}} แล้ว',
  pa_headless_fx_record: 'บันทึกการอนุมัติ',
  pa_headless_fx_check: 'ตรวจ {{currency}} วันนี้',
  pa_headless_fx_allowed: 'อนุญาต',
  pa_headless_fx_not_allowed: 'ไม่อนุญาต',
  pa_headless_ret_label: 'ระยะเวลาเก็บรักษา',
  pa_headless_ret_5y: '5 ปี (ขั้นต่ำตามมาตรา 14)',
  pa_headless_ret_6y: '6 ปี',
  pa_headless_ret_7y: '7 ปี (สูงสุดที่ DBD ขยายได้)',
  pa_headless_ret_th_when: 'เมื่อ',
  pa_headless_ret_th_what: 'รายการ',
  pa_headless_ret_th_mode: 'วิธี',
  pa_headless_ret_th_reason: 'เหตุผล',
  pa_headless_ret_th_by: 'โดย',
  pa_headless_ret_none: 'ยังไม่มีการทำลายเอกสาร',
  pa_headless_agm_registered: 'จดทะเบียน',
  pa_headless_agm_first_due: 'ประชุมครั้งแรกภายใน',
  pa_headless_agm_last_held: 'ประชุมครั้งล่าสุด',
  pa_headless_agm_next_due: 'ครั้งถัดไปภายใน',
  pa_headless_agm_overdue: 'เกินกำหนด',
  pa_headless_wht_settled: '{{n}} ชำระแล้ว',
  pa_headless_wht_payer_alone: 'ผู้จ่ายรับผิดแต่ผู้เดียว',
  pa_headless_wht_joint: 'รับผิดร่วม',
  pa_headless_wht_at_risk: 'ภาษีที่ยังมีความเสี่ยงรวม {{amount}} บาท',
  pa_headless_wht_th_paid: 'วันจ่าย',
  pa_headless_wht_th_payee: 'ผู้รับเงิน',
  pa_headless_wht_th_form: 'แบบ',
  pa_headless_wht_th_gross: 'ยอดจ่าย',
  pa_headless_wht_th_withheld: 'หักภาษี',
  pa_headless_wht_th_who: 'ใครรับผิด',
  pa_headless_wht_th_why: 'เหตุผล',
  pa_headless_wht_all_clear: 'ทุกบรรทัดหักและนำส่งแล้ว',
  pa_headless_sso_rule_a: 'บริษัทที่จดทะเบียนใหม่เป็นนายจ้างโดยผลของกฎหมาย และต้องขึ้นทะเบียนลูกจ้างเข้าประกันสังคม เลขประกันสังคมของลูกจ้างแต่ละคนมีอยู่แล้ว แต่บัญชี',
  pa_headless_sso_rule_b: 'ของนายจ้างเอง',
  pa_headless_sso_rule_c: 'ยังไม่มี',
  pa_headless_sso_account_no: 'เลขที่บัญชีนายจ้าง',
  pa_headless_sso_branch: 'สาขา',
  pa_headless_sso_registered_on: 'ขึ้นทะเบียน',
  pa_headless_sso_form_101: 'สปส.1-01',
  pa_headless_sso_office: 'สำนักงานเขตพื้นที่',
  pa_headless_sso_saved: 'บันทึกแล้ว',
  pa_headless_sso_save: 'บันทึก',
  pa_headless_sso_employees: '{{n}} พนักงาน',
  pa_headless_sso_no_number: '{{n}} คนยังไม่มีเลขประกันสังคม',
  pa_headless_rev_rule_a: 'Revenue Code s.65 bis (3)-(4) — ผลกำไรจากการตีราคาสินทรัพย์เพิ่มไม่ถือเป็นกำไรที่ต้องเสียภาษี',
  pa_headless_rev_rule_b: ' และ',
  pa_headless_rev_rule_c: 'ไม่นับเป็นฐานค่าเสื่อม จึงทำให้ฐานทางบัญชีกับฐานทางภาษีแยกจากกันอย่างถาวร',
  pa_headless_rev_th_asset: 'ทรัพย์สิน',
  pa_headless_rev_th_cost: 'ราคาทุน',
  pa_headless_rev_th_book: 'ฐานทางบัญชี',
  pa_headless_rev_th_tax: 'ฐานทางภาษี',
  pa_headless_rev_th_reval: 'การตีราคาสินทรัพย์เพิ่ม',
  pa_headless_rev_amount_prompt: 'มูลค่าตามบัญชีใหม่ของ {{name}} (baht)?',
  pa_headless_rev_date_prompt: 'ตีราคาใหม่เมื่อ (YYYY-MM-DD)?',
  pa_headless_rev_basis_prompt: 'ใครประเมินราคา และตามรายงานฉบับใด (ต้องระบุ)',
  pa_headless_rev_revalue: 'ตีราคาใหม่…',
  pa_headless_rev_empty: 'ไม่มีทรัพย์สินในทะเบียน',
  pa_headless_module_title: 'รายการที่เหลือ',
  pa_headless_sec_wa_name: 'บัญชีทำการ',
  pa_headless_sec_wa: 'Working account (s.68 bis)',
  pa_headless_sec_wa_sub: 'สร้างไว้แล้ว แต่เพิ่งมีปุ่มเข้าใช้',
  pa_headless_sec_fw_name: 'แม่บทการรายงานทางการเงิน',
  pa_headless_sec_fw: 'Reporting framework',
  pa_headless_sec_fw_sub: 'บันทึกไว้กับรอบระยะเวลาบัญชี — รอบที่ปิดบัญชีแล้วไม่มีการปรับปรุงย้อนหลัง',
  pa_headless_sec_sh_name: 'ทะเบียนผู้ถือหุ้น',
  pa_headless_sec_sh: 'Share register / บอจ.5',
  pa_headless_sec_sh_sub: 'การถือหุ้น ณ วันที่ที่ระบุ พร้อมสายการโอน',
  pa_headless_sec_pkg: 'ชุดเอกสารยื่น DBD e-Filing',
  pa_headless_sec_pkg_sub: 'สามช่องเอกสาร การระบุหน่วย และสิ่งที่ระบบนี้ผลิตไม่ได้',
  pa_headless_sec_cit: 'ภาษีเงินได้: หักได้ / หักไม่ได้ (s.65 ter)',
  pa_headless_sec_cit_sub: 'เฉพาะอนุมาตรา (1)–(6) — ส่วนที่เหลือยังไม่ได้มา',
  pa_headless_sec_fx_name: 'ใบกำกับภาษีสกุลเงินต่างประเทศ',
  pa_headless_sec_fx: 'Foreign-currency approval (s.86/4)',
  pa_headless_sec_fx_sub: '⚠️ บังคับใช้แล้วเมื่อออกหรือแก้ไขใบกำกับภาษี',
  pa_headless_sec_ret: 'ระยะเวลาเก็บรักษาและทะเบียนการทำลาย (s.14)',
  pa_headless_sec_ret_sub: 'กลไกมีอยู่แล้วแต่ไม่มีเส้นทางเรียกใช้เลย',
  pa_headless_sec_agm: 'การประชุมใหญ่ (CCC s.1197)',
  pa_headless_sec_agm_sub: 'ครั้งแรกภายใน 6 เดือนนับจากจดทะเบียน จากนั้นทุก 12 เดือน',
  pa_headless_sec_wht: 'ความรับผิดของผู้จ่ายเงิน (s.54)',
  pa_headless_sec_wht_sub: 'หักแล้วไม่นำส่ง ผู้จ่ายรับผิดแต่ผู้เดียว; ไม่ได้หักเลย รับผิดร่วม',
  pa_headless_sec_sso: 'ขึ้นทะเบียนนายจ้าง',
  pa_headless_sec_sso_sub: 'บัญชีของนายจ้างเอง ไม่ใช่ของลูกจ้าง',
  pa_headless_sec_rev: 'การตีราคาสินทรัพย์เพิ่ม: ฐานทางบัญชีกับฐานทางภาษี (s.65 bis)',
  pa_headless_sec_rev_sub: 'ผลกำไรนั้นอยู่นอกกำไรที่ต้องเสียภาษี และอยู่นอกฐานค่าเสื่อม',

  // ── Registers — the statutory registers ───────────────────────────────────────
  pa_reg_title: 'ทะเบียนตามกฎหมาย',
  pa_reg_record: 'บันทึก',
  pa_reg_save: 'บันทึก',
  pa_reg_saved: 'บันทึกแล้ว',
  pa_reg_empty: 'ยังไม่มีรายการในทะเบียน',
  pa_reg_overdue: 'เกินกำหนด',
  pa_reg_days_late: 'เกินกำหนด {{n}} วัน',
  pa_reg_doc_directors_report: 'รายงานของกรรมการ',
  pa_reg_doc_firm_register: 'ทะเบียนสำนักงานบัญชี',
  pa_reg_doc_liquidation_report: 'รายงานการชำระบัญชี',
  pa_reg_doc_sales_tax_invoice: 'ใบกำกับภาษีขาย',
  pa_reg_incident_kind_lost: 'สูญหาย',
  pa_reg_incident_kind_damaged: 'เสียหาย',
  pa_reg_incident_discovered: 'พบเมื่อ',
  pa_reg_incident_happened: 'เกิดเมื่อ',
  pa_reg_incident_scope_ph: 'สมุดบัญชี / เอกสารใด',
  pa_reg_incident_cause_ph: 'สาเหตุ (s.16: เว้นว่าง = ถือว่าเจตนา)',
  pa_reg_incident_presumed_alert: 's.16: การสูญหายที่ไม่ได้ระบุสาเหตุ ถือว่าเป็นการกระทำโดยเจตนา บันทึกสิ่งที่เกิดขึ้นทันทีที่ทราบ',
  pa_reg_incident_th_discovered: 'พบเมื่อ',
  pa_reg_incident_th_what: 'ลักษณะ',
  pa_reg_incident_th_scope: 'ขอบเขต',
  pa_reg_incident_th_cause: 'สาเหตุ',
  pa_reg_incident_th_notify_by: 'แจ้งภายใน',
  pa_reg_incident_th_notified: 'แจ้งแล้ว',
  pa_reg_incident_recovered: 'ได้คืนแล้ว',
  pa_reg_incident_cause_blank: 'ไม่ได้ระบุ — s.16 ถือว่าเจตนา',
  pa_reg_incident_notified_prompt: 'แจ้ง DBD เมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_incident_epermit_prompt: 'เลขอ้างอิง e-Permit?',
  pa_reg_incident_notified_btn: 'แจ้งแล้ว…',
  pa_reg_incident_unexplained: 'รายการที่ไม่ได้ระบุสาเหตุ: {{n}} รายการ s.16 ถือว่าการสูญหายที่อธิบายไม่ได้เป็นการกระทำโดยเจตนา — บันทึกสิ่งที่เกิดขึ้น',
  pa_reg_cess_ceased: 'เลิกประกอบกิจการเมื่อ',
  pa_reg_cess_reason_ph: 'เหตุผล',
  pa_reg_cess_record: 'บันทึกการเลิกประกอบธุรกิจ',
  pa_reg_cess_ceased_on: 'เลิกกิจการ {{date}}',
  pa_reg_cess_deliver_by: 'ส่งมอบภายใน',
  pa_reg_cess_extended_to: 'ขยายถึง {{date}}',
  pa_reg_cess_max_extension: '(ขยายได้ถึง {{date}} เป็นอย่างช้า)',
  pa_reg_cess_delivered: 'ส่งมอบแล้ว {{date}}',
  pa_reg_cess_not_delivered: 'ยังไม่ส่งมอบ',
  pa_reg_cess_overdue_days: 'เกินกำหนด {{n}} วัน',
  pa_reg_cess_extend_prompt: 'ขยายถึง (YYYY-MM-DD, อย่างช้า {{date}})?',
  pa_reg_cess_extend_ref_prompt: 'เลขอ้างอิงการขยายเวลา?',
  pa_reg_cess_extend_btn: 'ได้รับการขยายเวลา…',
  pa_reg_cess_delivered_prompt: 'ส่งมอบให้ DBD เมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_cess_receipt_prompt: 'เลขอ้างอิงการรับมอบของ DBD?',
  pa_reg_cess_delivered_btn: 'ส่งมอบแล้ว…',
  pa_reg_insp_demanded: 'วันที่เรียก',
  pa_reg_insp_authority_ph: 'หน่วยงาน',
  pa_reg_insp_inspector_ph: 'พนักงานเจ้าหน้าที่',
  pa_reg_insp_due: 'กำหนดส่ง',
  pa_reg_insp_record: 'บันทึกการเรียกตรวจ',
  pa_reg_insp_asked_label: 'ขอให้ส่ง',
  pa_reg_insp_preview: 'ดูตัวอย่างรายการเอกสาร',
  pa_reg_insp_build: 'สร้างชุดเอกสาร',
  pa_reg_insp_th_demanded: 'วันที่เรียก',
  pa_reg_insp_th_by: 'โดย',
  pa_reg_insp_th_asked: 'ขอให้ส่ง',
  pa_reg_insp_th_due: 'กำหนดส่ง',
  pa_reg_insp_th_answered: 'ส่งแล้ว',
  pa_reg_insp_answered_prompt: 'ส่งเมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_insp_answered_btn: 'ส่งแล้ว…',
  pa_reg_chg_resolved: 'มติเมื่อ',
  pa_reg_chg_from_ph: 'จาก',
  pa_reg_chg_to_ph: 'เป็น',
  pa_reg_chg_liquidation_due: 'รายงานฉบับถัดไปต้องส่งภายใน {{due}} (สามเดือนนับจาก {{since}})',
  pa_reg_chg_th_resolved: 'มติเมื่อ',
  pa_reg_chg_th_what: 'เรื่อง',
  pa_reg_chg_th_fromto: 'จาก → เป็น',
  pa_reg_chg_th_register_by: 'จดทะเบียนภายใน',
  pa_reg_chg_th_registered: 'จดทะเบียนแล้ว',
  pa_reg_chg_registered_prompt: 'จดทะเบียนกับ DBD เมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_chg_receipt_prompt: 'เลขที่หลักฐานการรับจดทะเบียน?',
  pa_reg_chg_registered_btn: 'จดทะเบียนแล้ว…',
  pa_reg_dr_meeting: 'วันประชุม',
  pa_reg_dr_presented: 'เสนอเมื่อ',
  pa_reg_dr_body_th_label: 'รายงานของกรรมการ (ไทย)',
  pa_reg_dr_body_en_label: 'รายงานของกรรมการ (อังกฤษ)',
  pa_reg_vat_rev12: 'รายได้ 12 เดือน {{from}} – {{to}}',
  pa_reg_vat_source: 'จาก {{source}}',
  pa_reg_vat_source_ledger: 'สมุดบัญชี',
  pa_reg_vat_exceeded_head: 'เกิน 1.8 ล้านบาทแล้ว.',
  pa_reg_vat_exceeded_body: 'รายได้ 12 เดือนย้อนหลังเกินเกณฑ์จดทะเบียนแล้ว',
  pa_reg_vat_notice_ack_by: 'ยืนยันแล้ว — ยืนยันโดย',
  pa_reg_vat_notice_ack_tail: ' จึงหยุดแจ้งเตือนรายวันแล้ว',
  pa_reg_vat_notice_sending: 'แจ้งเตือนทุกวัน — ส่งไปที่ Telegram และ office@slf-accounting.co แล้ว {{n}} ครั้ง',
  pa_reg_vat_notice_sending_last: 'แจ้งเตือนทุกวัน — ส่งไปที่ Telegram และ office@slf-accounting.co แล้ว {{n}} ครั้ง ครั้งล่าสุด {{date}}',
  pa_reg_vat_notice_stops: 'จะหยุดส่งเมื่อมีผู้คลิกลิงก์ในอีเมล',
  pa_reg_vat_notice_last_send: 'การส่งครั้งล่าสุด:',
  pa_reg_vat_notice_resume: 'เริ่มส่งอีกครั้ง',
  pa_reg_stock_cost_formula: 'วิธีคำนวณต้นทุน',
  pa_reg_stock_method_none: '— ยังไม่ระบุ —',
  pa_reg_stock_th_item: 'สินค้า',
  pa_reg_stock_th_qty: 'จำนวน',
  pa_reg_stock_th_cost: 'ราคาทุน',
  pa_reg_stock_th_market: 'ราคาตลาด',
  pa_reg_stock_th_carried: 'มูลค่าที่บันทึก',
  pa_reg_stock_th_writedown: 'ผลต่างจากการตีราคาลด',
  pa_reg_stock_total: 'รวม',
  pa_reg_stock_empty: 'ไม่มีสินค้าคงเหลือที่ต้องตีราคา',
  pa_reg_stock_struck_msg: 'ตีราคาแล้ว ผลต่างจากการตีราคาลด {{amount}} อยู่ในรายการรอตรวจ {{doc}} — ตรวจสอบพร้อมรายการรอตรวจอื่น',
  pa_reg_stock_struck_none: 'ตีราคาแล้ว ไม่มีผลต่างจากการตีราคาลด',
  pa_reg_stock_strike_btn: 'ตีราคาสินค้าคงเหลือ',
  pa_reg_stock_struck_line: 'ตีราคาแล้ว {{n}} รายการ · ตีราคาเมื่อ {{date}} โดย {{name}}',
  pa_reg_firm_rule: 'Accounting Professions Act s.11 — นิติบุคคลที่ให้บริการด้านบัญชีต้องจดทะเบียนกับ TFAC และวางหลักประกันความรับผิดต่อบุคคลที่สาม ผู้ที่ให้บริการสอบบัญชีต้องมีผู้สอบบัญชีรับอนุญาต (CPA) เป็นผู้ผูกพัน และรับผิดร่วมกับผู้สอบบัญชีของตน',
  pa_reg_firm_regno_ph: 'เลขทะเบียน TFAC',
  pa_reg_firm_registered: 'จดทะเบียน',
  pa_reg_firm_expires: 'หมดอายุ',
  pa_reg_firm_guarantee_ph: 'หลักประกันที่วาง (บาท)',
  pa_reg_firm_guarantee_form_ph: 'รูปแบบ (หนังสือค้ำประกันธนาคาร…)',
  pa_reg_firm_offers_audit: 'ให้บริการสอบบัญชี',
  pa_reg_firm_cpa_name_ph: 'ผู้สอบบัญชีรับอนุญาต (CPA) ผู้ลงลายมือชื่อ',
  pa_reg_firm_cpa_no_ph: 'เลขทะเบียน CPA',
  pa_reg_firm_expired: 'หมดอายุแล้ว — ทะเบียนนี้หมดอายุเมื่อ {{date}}',
  pa_reg_firm_expiring: 'หมดอายุ {{date}} — ภายใน 60 วัน',
  pa_reg_sec_incidents_title: 'บัญชีหรือเอกสารสูญหาย/เสียหาย (ss.15-16)',
  pa_reg_sec_incidents_sub: 'แจ้งภายใน 15 วันนับจากวันที่พบ การสูญหายที่อธิบายไม่ได้ถือว่าเป็นการกระทำโดยเจตนา',
  pa_reg_sec_cess_title: 'เลิกประกอบธุรกิจ (s.17)',
  pa_reg_sec_cess_sub: 'ส่งมอบสมุดบัญชีให้ DBD ภายใน 90 วัน ขยายได้ถึง 180 วัน',
  pa_reg_sec_insp_title: 'พนักงานเจ้าหน้าที่เรียกตรวจ (ss.22, 24, 25)',
  pa_reg_sec_insp_sub: 'บันทึกการเรียกตรวจ และรวมสมุดบัญชี ใบสำคัญ และคู่มือรหัสบัญชีเป็นชุดเดียว',
  pa_reg_sec_chg_title: 'จดทะเบียนเปลี่ยนแปลง (14 วัน)',
  pa_reg_sec_chg_sub: 'ทุน ข้อบังคับ กรรมการ และรอบรายงานการชำระบัญชีทุกสามเดือน',
  pa_reg_sec_dr_title_gloss: 'Directors\' report (CCC s.1198)',
  pa_reg_sec_dr_sub: 'เสนอต่อการประชุมใหญ่พร้อมงบดุล ไม่มีแบบที่กำหนดไว้',
  pa_reg_sec_stock_title: 'ตีราคาสินค้าคงเหลือ ราคาทุนหรือราคาตลาดที่ต่ำกว่า (s.65 bis)',
  pa_reg_sec_stock_sub: 'เข้าก่อนออกก่อน (FIFO) หรือถัวเฉลี่ยถ่วงน้ำหนัก ล็อกเมื่อใช้แล้ว (มูลค่าเข้าลบมูลค่าออกไม่ใช่วิธีคำนวณต้นทุน)',
  pa_reg_sec_vat_title: 'เกณฑ์จดทะเบียน VAT 1.8 ล้าน',
  pa_reg_sec_vat_sub: 'รายได้ 12 เดือนย้อนหลังเทียบกับ 1,800,000 บาท',
  pa_reg_sec_firm_title_gloss: 'The firm\'s own TFAC registration (APA s.11)',
  pa_reg_sec_firm_sub: 'เกี่ยวกับ SLF เอง ไม่ใช่ลูกค้า: การจดทะเบียน หลักประกัน และผู้สอบบัญชีรับอนุญาต (CPA) ที่ผูกพันสำนักงาน',

  // ── TaxLedgers — the six tax ledgers ──────────────────────────────────────────
  pa_tax_channel_label: 'ยื่นแบบทาง',
  pa_tax_channel_online: 'ยื่นออนไลน์',
  pa_tax_channel_paper: 'ยื่นกระดาษ',
  pa_tax_channel_deadlines_paper: 'แบบรายเดือนกำหนดภายในวันที่ 7 (ภาษีมูลค่าเพิ่มวันที่ 15); PND.50 150 วัน, PND.51 สองเดือน — ไม่ได้รับการขยายเวลาของการยื่นออนไลน์',
  pa_tax_channel_deadlines_online: 'แบบรายเดือนกำหนดภายในวันที่ 15 (ภาษีมูลค่าเพิ่มวันที่ 23); PND.50 158 วัน, PND.51 สองเดือนแปดวัน',
  pa_tax_fx_rule_1: 's.65 bis (5) — ยอดคงเหลือสิ้นปีแปลงค่าด้วยอัตราของธนาคารแห่งประเทศไทย โดยใช้วิธีเดียวอย่างสม่ำเสมอ ระบบนี้ไม่มีแหล่งอัตรา อัตราต้องนำเข้า และยอดคงเหลือที่ไม่มีอัตราในวันสิ้นปีหรือก่อนนั้น ',
  pa_tax_fx_rule_not: 'ไม่',
  pa_tax_fx_rule_2: 'ถูกแปลงค่าด้วยอัตราใดเลย',
  pa_tax_fx_method_label: 'วิธีแปลงค่า',
  pa_tax_fx_other_method_prompt: 'วิธีที่ได้รับอนุมัติคือวิธีใด และใครอนุมัติ?',
  pa_tax_fx_method_none: '— ยังไม่ระบุ —',
  pa_tax_fx_no_method_warn: 'ยังไม่ได้ล็อกวิธีแปลงค่า s.65 bis (5) กำหนดให้ใช้วิธีเดียวอย่างสม่ำเสมอ จึงจะไม่มีการตีราคาใหม่จนกว่าจะเลือกวิธี — และเมื่อมีรอบระยะเวลาบัญชีที่ตีราคาใหม่ด้วยวิธีนั้นแล้ว จะเปลี่ยนวิธีไม่ได้',
  pa_tax_fx_rate_label: 'อัตรา',
  pa_tax_fx_kind_avg: 'อัตราเฉลี่ย (BOT)',
  pa_tax_fx_kind_buy: 'อัตราซื้อ (BOT)',
  pa_tax_fx_kind_sell: 'อัตราขาย (BOT)',
  pa_tax_fx_source_ph: 'ที่มาของอัตรา',
  pa_tax_fx_rate_stored: 'บันทึกอัตราแล้ว',
  pa_tax_fx_nothing_imported: 'ไม่มีการนำเข้า',
  pa_tax_fx_add_rate: 'เพิ่มอัตรา',
  pa_tax_fx_account_none: '— บัญชี —',
  pa_tax_fx_amount_fx_ph: 'จำนวนเงินตราต่างประเทศ',
  pa_tax_fx_booked_ph: 'บาทตามบัญชี',
  pa_tax_fx_add_balance: 'เพิ่มยอดเงินตราต่างประเทศ',
  pa_tax_fx_th_account: 'บัญชี',
  pa_tax_fx_th_cur: 'สกุล',
  pa_tax_fx_th_amount: 'จำนวน',
  pa_tax_fx_th_booked: 'ยอดตามบัญชี',
  pa_tax_fx_th_rate: 'อัตราแลกเปลี่ยน',
  pa_tax_fx_th_at_rate: 'ตามอัตรา',
  pa_tax_fx_th_diff: 'กำไร / (ขาดทุน)',
  pa_tax_fx_empty: 'ไม่มียอดเงินตราต่างประเทศบันทึกไว้ในรอบระยะเวลาบัญชีนี้',
  pa_tax_fx_revalued_msg: 'รายการรอตรวจ {{doc}} — สุทธิ {{amount}} · ยอดที่ตีราคาใหม่: {{n}} ตรวจสอบพร้อมรายการรอตรวจอื่นก่อนผ่านรายการ',
  pa_tax_fx_revalue_btn: 'ปรับปรุงสิ้นปี {{amount}}',
  pa_tax_rem_warn_1: 'ระบบนี้',
  pa_tax_rem_warn_norate: 'ไม่มีอัตรา',
  pa_tax_rem_warn_2: ' สำหรับทั้งสองแบบ และไม่มีฐานทางกฎหมายของอัตรา อัตราตาม ท.ป.4/2528 ที่ใช้กับการจ่ายในประเทศเป็นอีกบทบัญญัติหนึ่ง ห้ามใช้ที่นี่ — ทุกบรรทัดใช้อัตราที่ผู้ให้คำปรึกษากำหนด และบันทึกผู้ที่กรอกไว้',
  pa_tax_rem_lines: '{{n}} บรรทัด',
  pa_tax_rem_base: 'ฐาน {{amount}}',
  pa_tax_rem_tax: 'ภาษี',
  pa_tax_rem_due: 'กำหนดภายใน {{date}}',
  pa_tax_rem_filed: 'ยื่นแล้ว',
  pa_tax_rem_payee_ph: 'ผู้รับเงิน',
  pa_tax_rem_country_ph: 'ประเทศ',
  pa_tax_rem_nature_ph: 'ลักษณะเงินได้',
  pa_tax_rem_base_ph: 'ฐาน (บาท)',
  pa_tax_rem_rate_ph: 'อัตรา %',
  pa_tax_rem_basis_ph: 'ฐานทางกฎหมาย',
  pa_tax_rem_add: 'เพิ่ม',
  pa_tax_rem_th_paid: 'วันจ่าย',
  pa_tax_rem_th_payee: 'ผู้รับเงิน',
  pa_tax_rem_th_for: 'ลักษณะเงินได้',
  pa_tax_rem_th_base: 'ฐาน',
  pa_tax_rem_th_rate: 'อัตรา',
  pa_tax_rem_th_tax: 'ภาษี',
  pa_tax_rem_th_basis: 'ฐานกฎหมาย',
  pa_tax_rem_not_recorded: 'ยังไม่ได้บันทึก',
  pa_tax_rem_empty: 'ไม่มีรายการในแบบของเดือนนี้',
  pa_tax_rem_filed_prompt: 'นำส่งเมื่อ (YYYY-MM-DD)?',
  pa_tax_rem_mark_filed: 'ทำเครื่องหมายยื่นแล้ว',
  pa_tax_stamp_rule: 'ss.104 และ 118 — ตราสารที่อยู่ในบัญชีอัตราต้องปิดแสตมป์ให้ครบถ้วน และตราสารที่ไม่ปิดแสตมป์ใช้เป็นพยานหลักฐานไม่ได้จนกว่าจะชำระค่าอากรและเงินเพิ่ม สำนักงานในฐานะผู้รับจ้างตามสัญญาจ้างของตนเองมีหน้าที่เสียอากร 1 บาท ต่อ 1,000 — 0.1%',
  pa_tax_stamp_party_ph: 'คู่สัญญา',
  pa_tax_stamp_base_ph: 'ค่าเช่า / ค่าธรรมเนียม / เงินต้น',
  pa_tax_stamp_pick_none: '— จำนวนใด —',
  pa_tax_baht_amount: '{{amount}} บาท',
  pa_tax_stamp_one_act: ' (ทำครั้งเดียว)',
  pa_tax_stamp_several_acts: ' (ทำหลายครั้ง)',
  pa_tax_stamp_vat_exempt: 'มี VAT บนจำนวนนี้ (ยกเว้น)',
  pa_tax_stamp_record: 'บันทึก',
  pa_tax_stamp_schedule_head: 'ลักษณะแห่งตราสาร',
  pa_tax_stamp_number_inferred: ' (เลขที่ลักษณะเป็นการอนุมาน — แหล่งที่มาให้ทั้งเจ็ดลักษณะมาเป็นชุด)',
  pa_tax_stamp_band: ' · บัญชีอัตรากำหนดไว้ {{min}}–{{max}} บาท เป็นช่วง ไม่ใช่สูตรคำนวณ',
  pa_tax_stamp_th_date: 'วันที่',
  pa_tax_stamp_th_instrument: 'ตราสาร',
  pa_tax_stamp_th_party: 'คู่สัญญา',
  pa_tax_stamp_th_base: 'ฐาน',
  pa_tax_stamp_th_duty: 'ค่าอากร',
  pa_tax_stamp_th_stamped: 'ปิดอากร',
  pa_tax_stamp_not_established: 'ยังไม่ได้กำหนด',
  pa_tax_stamp_exempt: 'ยกเว้น',
  pa_tax_stamp_unpaid: 'ยังไม่ปิด',
  pa_tax_stamp_method_prompt: 'ชำระด้วยวิธีใด? พิมพ์ adhesive, impressed หรือ cash',
  pa_tax_stamp_paid_on_prompt: 'ชำระเมื่อ (YYYY-MM-DD)?',
  pa_tax_stamp_duty_prompt: 'ค่าอากรที่ชำระ (บาท)?',
  pa_tax_stamp_receipt_prompt: 'เลขที่ใบรับ อ.ส.?',
  pa_tax_stamp_mark_btn: 'บันทึกปิดอากร',
  pa_tax_stamp_empty: 'ไม่มีรายการในทะเบียน',
  pa_tax_stamp_unstamped_count: 'ยังไม่ปิดอากร {{n}} ฉบับ',
  pa_tax_stamp_duty_established: 'ค่าอากรที่กำหนดแล้ว: {{amount}}',
  pa_tax_stamp_unquantified: ' · อีก {{n}} ฉบับยังไม่มีจำนวนค่าอากร',
  pa_tax_loss_year_ph: 'ปี',
  pa_tax_loss_amount_ph: 'ขาดทุน (บาท)',
  pa_tax_loss_note_ph: 'หมายเหตุ',
  pa_tax_loss_record: 'บันทึกผลขาดทุน',
  pa_tax_loss_years_prompt: 'ระยะเวลายกไป กี่ปี?',
  pa_tax_loss_basis_prompt: 'มาจากบทบัญญัติใด? (ต้องระบุ)',
  pa_tax_loss_set_period: 'กำหนดระยะเวลา…',
  pa_tax_loss_th_year: 'ปี',
  pa_tax_loss_th_loss: 'ขาดทุน',
  pa_tax_loss_th_used: 'ใช้แล้ว',
  pa_tax_loss_th_left: 'คงเหลือ',
  pa_tax_loss_th_expires: 'หมดอายุ',
  pa_tax_loss_th_used_in: 'ใช้ในปี',
  pa_tax_loss_use_year_prompt: 'ใช้ในปีใด?',
  pa_tax_loss_use_amount_prompt: 'ใช้เท่าใด (บาท)?',
  pa_tax_loss_use_btn: 'ใช้…',
  pa_tax_loss_total: 'รวม',
  pa_tax_loss_empty: 'ไม่มีผลขาดทุนในทะเบียน',
  pa_tax_loss_unreconciled: 'ยอดรวมที่บันทึกไว้กับผลรวมของการใช้แต่ละครั้งไม่ตรงกัน ยังไม่ควรนำตัวเลขในหน้านี้ไปยื่นจนกว่าจะตรวจสอบให้ตรงกัน',
  pa_tax_rc_no_threshold: 'ยังไม่ได้บันทึกเกณฑ์ การตรวจนี้จึงไม่ทำงาน s.105 ให้ไว้เพียงเพดาน — 1,000 บาทสำหรับผู้ประกอบการจดทะเบียน VAT/SBT และ 10,000 สำหรับรายอื่น — และจำนวนที่อธิบดีประกาศกำหนดไม่ได้เก็บไว้ในระบบนี้ โปรดบันทึกจำนวนนั้น หรือบันทึก 0 เพื่อให้หมายถึงทุกการรับเงิน ซึ่งเป็นการตีความที่ปลอดภัยในขณะที่ยังไม่ทราบจำนวน',
  pa_tax_rc_registrant_prompt: 'เป็นผู้ประกอบการจดทะเบียน VAT/SBT หรือไม่? พิมพ์ vat_sbt หรือ other',
  pa_tax_rc_threshold_prompt: 'เกณฑ์เป็นบาท (0 = ทุกการรับเงิน; เพดาน {{ceiling}})',
  pa_tax_rc_notification_prompt: 'เลขที่ประกาศอธิบดี ถ้าสำนักงานมี',
  pa_tax_rc_change_threshold: 'แก้ไขเกณฑ์…',
  pa_tax_rc_record_threshold: 'บันทึกเกณฑ์…',
  pa_tax_rc_registrant_yes: 'ผู้ประกอบการจดทะเบียน VAT/SBT',
  pa_tax_rc_registrant_no: 'ไม่ได้จดทะเบียน',
  pa_tax_rc_scope_level: 'ระดับ {{scope}}',
  pa_tax_rc_th_received: 'วันรับเงิน',
  pa_tax_rc_th_against: 'เอกสารอ้างอิง',
  pa_tax_rc_th_amount: 'จำนวน',
  pa_tax_rc_th_desc: 'คำอธิบายรายการ',
  pa_tax_rc_all_covered: 'ทุกการรับเงินที่จับคู่แล้วในช่วงนี้มีใบรับรองรับ (ตรวจแล้ว {{n}} รายการ)',
  pa_tax_rc_daily_head: 'ยอดรวมรายวัน s.105 ter',
  pa_tax_rc_daily_body: '{{n}} วัน รวม {{amount}}',
  pa_tax_title: 'ภาษีที่เกี่ยวข้อง',
  pa_tax_sec_fx_title: 'อัตราแลกเปลี่ยนและการปรับปรุงสิ้นปี',
  pa_tax_sec_fx_sub: 's.65 bis (5) — ใช้วิธีเดียวอย่างสม่ำเสมอ ถ้าไม่มีอัตราที่นี่ ก็ไม่มีการตีราคาใหม่',
  pa_tax_sec_rem_forms: 'ภ.ง.ด.54 · ภ.พ.36',
  pa_tax_sec_rem_title: 'การนำส่งภาษี',
  pa_tax_sec_rem_sub: 'เป็นทะเบียน ไม่ใช่ตัวคำนวณอัตรา ระบบนี้ไม่ทราบอัตราของทั้งสองแบบ',
  pa_tax_sec_stamp_title: 'อากรแสตมป์',
  pa_tax_sec_stamp_sub: 'อัตราตามบัญชีอัตราเจ็ดลักษณะที่สำนักงานกฎหมายหรือสำนักงานบัญชีพบ และส่วนที่ยังไม่ได้กำหนด',
  pa_tax_sec_loss_title: 'ทะเบียนผลขาดทุนยกไป',
  pa_tax_sec_loss_sub: 'หนึ่งแถวต่อหนึ่งปีที่ขาดทุน และไม่ระบุวันหมดอายุจนกว่าจะบันทึกระยะเวลายกไป',
  pa_tax_sec_rc_doc: 'ใบรับ',
  pa_tax_sec_rc_title: 'ออกใบรับทุกครั้งที่รับเงิน (s.105)',
  pa_tax_sec_rc_sub: 'เงินรับที่ไม่มีใบรับรองรับ จะไม่ทำงานจนกว่าจะบันทึกเกณฑ์',
  pa_cat_sec_title: 'คลังสินค้าของลูกค้าเทียบกับบัญชีสินค้า',
  pa_cat_sec_sub: 'สินค้าที่ลูกค้าขายคือรายการในบัญชีสินค้าตามกฎหมาย รายการที่มีอยู่ในฝ่ายเดียวคือการขายที่ไม่มีต้นทุนบันทึกไว้ — ควรตรวจก่อนปิดบัญชีประจำเดือน ไม่ใช่หลังจากนั้น',
  pa_cat_rule: 'ลูกค้าดูแลรายการสินค้าในพอร์ทัลของตนเอง และ บัญชีสินค้า คือสมุดบัญชีตามกฎหมายที่สินค้านั้นถูกบันทึกไว้ รายการตามกฎหมายเป็นต้นแบบ รหัสและชื่อภาษาไทยของรายการจะถูกแก้จากรายการราคาไม่ได้',
  pa_cat_clean: 'ทั้งสองรายการตรงกัน — ทุกอย่างที่ลูกค้าขายอยู่ในบัญชีสินค้าแล้ว',
  pa_cat_n_gaps: 'ไม่ตรงกัน {{n}} รายการ',
  pa_cat_fix: 'เติมช่องว่าง {{n}} รายการที่ไม่ต้องขอข้อมูลจากใคร',
  pa_cat_fixed: 'แก้แล้ว {{n}} รายการ · ยังเหลือ {{left}} รายการที่ต้องมีคนตอบ',
  pa_cat_th_what: 'ประเภท',
  pa_cat_th_name: 'รายการ',
  pa_cat_th_why: 'เหตุผล',
  pa_cat_kind_product_missing_particulars: 'ข้อมูลไม่ครบ',
  pa_cat_kind_product_unlinked: 'ยังไม่เข้าสมุดบัญชี',
  pa_cat_kind_item_without_product: 'ลูกค้าออกใบกำกับไม่ได้',
  pa_cat_kind_product_undecided: 'สินค้าหรือบริการ',
  pa_cat_kind_no_merchant_tenant: 'ไม่มีพอร์ทัล',
  pa_cat_kind_drift: 'ไม่ตรงกัน',
  pa_imp_sec_title: 'นำเข้าความเคลื่อนไหวสินค้า',
  pa_imp_sec_sub: 'นำเข้าการรับและการจ่ายทั้งปีจากไฟล์ของลูกค้าเอง ท่านเป็นผู้ระบุว่าแต่ละคอลัมน์คืออะไร และคำว่ารับหรือจ่ายในไฟล์หมายถึงอะไร ระบบไม่เดา',
  pa_imp_rule: 'ความเคลื่อนไหวใช้ทั้งใน บัญชีสินค้า และ รายงานสินค้าและวัตถุดิบ การอัปโหลดไฟล์เดิมซ้ำปลอดภัย รายการที่นำเข้าแล้วจะถูกตรวจพบและไม่บันทึกซ้ำ',
  pa_imp_read: 'อ่านไฟล์',
  pa_imp_map_head: 'แต่ละคอลัมน์คืออะไร',
  pa_imp_th_col: 'คอลัมน์',
  pa_imp_th_sample: 'ตัวอย่างค่าแรก ๆ',
  pa_imp_th_is: 'คือ',
  pa_imp_f_ignore: '— ไม่นำเข้าคอลัมน์นี้ —',
  pa_imp_f_itemCode: 'รหัสสินค้า',
  pa_imp_f_itemName: 'ชื่อสินค้า (ใช้จับคู่)',
  pa_imp_f_itemNameTh: 'ชื่อภาษาไทย (สำหรับรายการใหม่)',
  pa_imp_f_unit: 'หน่วยนับ',
  pa_imp_f_itemType: 'ชนิด',
  pa_imp_f_moveDate: 'วันที่',
  pa_imp_f_direction: 'รับ / จ่าย (คอลัมน์เดียว)',
  pa_imp_f_quantity: 'จำนวน',
  pa_imp_f_qtyIn: 'จำนวนรับ',
  pa_imp_f_qtyOut: 'จำนวนจ่าย',
  pa_imp_f_value: 'มูลค่า (เฉพาะรายการรับ)',
  pa_imp_f_docNo: 'เลขที่เอกสาร',
  pa_imp_f_partyName: 'ผู้ขาย / ผู้ซื้อ',
  pa_imp_f_note: 'หมายเหตุ',
  pa_imp_dir_head: 'คำในไฟล์นี้หมายถึงอะไร',
  pa_imp_dir_hint: 'คำที่ยังไม่ระบุ แถวของคำนั้นจะถูกข้าม หากอ่านทิศทางกลับกัน การรับสินค้าจะกลายเป็นการขายโดยที่ยอดยังดุลอยู่ จึงไม่มีการสันนิษฐานใด ๆ ที่นี่',
  pa_imp_dir_unset: '— ยังไม่ระบุ —',
  pa_imp_dir_in: 'รับเข้า',
  pa_imp_dir_out: 'จ่ายออก',
  pa_imp_summary: 'พร้อมนำเข้า {{rows}} รายการ · ข้าม {{skipped}} แถว',
  pa_imp_create_items: 'เพิ่มรายการที่ยังไม่อยู่ในบัญชีสินค้าอีก {{n}} รายการด้วย',
  pa_imp_commit: 'นำเข้า',
  pa_imp_dropped: 'มีแถวจ่ายออก {{n}} แถวที่มีจำนวนเงิน จำนวนเงินนั้นจะไม่ถูกนำเข้า เพราะต้นทุนของการจ่ายออกคำนวณจากวิธีที่ลูกค้าเลือกไว้ ส่วนตัวเลขในไฟล์คือราคาขาย การนำเข้าตัวเลขนั้นไม่ได้เปลี่ยนผลการตีราคา และจะทำให้มูลค่าสินค้าคงเหลืออ่านเหมือนถูกหักด้วยรายได้',
  pa_imp_uncreatable: 'มี {{n}} แถวที่ระบุรายการซึ่งไม่อยู่ในบัญชีสินค้า และแถวนั้นไม่มีรหัส ชื่อภาษาไทย และหน่วยนับให้เพิ่มรายการได้ แถวเหล่านั้นถูกข้าม และไม่มีการสร้างข้อมูลขึ้นเอง',
  pa_imp_th_row: 'แถว',
  pa_imp_th_item: 'รายการ',
  pa_imp_th_skipwhy: 'เหตุผลที่ข้าม',
  pa_imp_done: 'นำเข้าแล้ว {{created}} · มีอยู่แล้ว {{dup}} · เพิ่มรายการใหม่ {{items}}',
  pa_imp_done_failed: 'มี {{n}} แถวที่บันทึกไม่ได้',
  pa_imp_row_n: 'แถวที่ {{n}}:',
  pa_imp_rerun_safe: 'แก้แถวเหล่านั้นในไฟล์แล้วอัปโหลดอีกครั้งได้เลย รายการที่เข้าไปแล้วจะถูกตรวจพบและไม่บันทึกซ้ำ',
  pa_mv_sec_title: 'บันทึกรายการสินค้าด้วยตนเอง',
  pa_mv_sec_sub: 'สำหรับรายการเดียวที่ไม่ได้อยู่ในไฟล์ และสำหรับแก้รายการที่บันทึกผิด',
  pa_mv_rule: 'รายการที่บันทึกด้วยตนเองอยู่ใน บัญชีสินค้า ร่วมกับรายการที่นำเข้าจากไฟล์และรายการที่มาจากใบกำกับภาษี ⛔ การแก้ไขทำโดยการกลับรายการ ไม่ใช่การลบ รายการนั้นอยู่ในสมุดบัญชีตามกฎหมาย รายการเดิมจึงคงอยู่ และรายการตรงกันข้ามจะบอกว่าเกิดอะไรขึ้นจริง',
  pa_mv_no_items: 'ลูกค้ารายนี้ยังไม่มีรายการสินค้า กรุณาเพิ่มในคลังสินค้าของลูกค้า หรือให้การนำเข้าไฟล์สร้างให้',
  pa_mv_f_item: 'รายการสินค้า',
  pa_mv_pick_item: '— เลือกรายการ —',
  pa_mv_on_hand: 'คงเหลือ {{n}} {{unit}}',
  pa_mv_f_date: 'วันที่เคลื่อนไหว',
  pa_mv_f_direction: 'รับ / จ่าย',
  pa_mv_f_qty: 'จำนวน',
  pa_mv_f_value: 'ราคาทุน (บาท)',
  pa_mv_no_value_on_out: 'รายการจ่ายออกไม่ต้องกรอกราคาทุน ต้นทุนคำนวณจากวิธีที่ลูกค้าเลือกไว้ (เข้าก่อนออกก่อน หรือถัวเฉลี่ยถ่วงน้ำหนัก) ตัวเลขที่กรอกที่นี่จะไม่เปลี่ยนผลการตีราคา และจะทำให้มูลค่าสินค้าคงเหลืออ่านเหมือนถูกหักด้วยรายได้',
  pa_mv_add: 'บันทึก',
  pa_mv_saved: 'บันทึกแล้ว',
  pa_mv_backdated: 'รายการนี้บันทึกย้อนหลัง มาตรา 87(3) ให้เวลาสามวันทำการนับจากวันที่เคลื่อนไหวถึงวันที่ลงบัญชี และรายงานจะแสดงว่าแต่ละรายการใช้เวลาเท่าใด',
  pa_mv_th_move: 'ทิศทาง',
  pa_mv_th_from: 'ที่มา',
  pa_mv_src_manual: 'บันทึกเอง',
  pa_mv_src_import: 'นำเข้าจากไฟล์',
  pa_mv_src_invoice: 'ใบกำกับภาษี',
  pa_mv_src_reversal: 'กลับรายการ',
  pa_mv_reverse: 'กลับรายการ',
  pa_mv_reverse_confirm: 'กลับรายการ {{qty}} × {{name}} นี้หรือไม่ รายการเดิมจะคงอยู่ในสมุดบัญชี และจะเพิ่มรายการตรงกันข้ามลงวันที่วันนี้ การทำรายการนี้ย้อนกลับไม่ได้ หากจำเป็นให้กลับรายการของรายการกลับนั้นอีกครั้ง',
  pa_mv_reversed: 'กลับรายการแล้ว รายการเดิมยังอยู่ในสมุดบัญชี พร้อมการแก้ไขอยู่ข้างกัน',
  pa_mv_already_reversed: 'รายการนี้ถูกกลับรายการไว้แล้ว ไม่มีการเพิ่มรายการใด',
  pa_mv_is_reversed: 'กลับรายการแล้ว',
};
